<template>
  <div class="leaflet-component-box">
    <slot></slot>
    <!-- 改进的图层排序操作面板 -->
    <div v-if="currentFenceId && isShowLayerSortPanel" class="layer-sort-panel">
      <div class="panel-title">{{langMap.layerSortOperation[this.lang]}}</div>

      <!-- 添加优先级直接设置区域 -->
      <div class="priority-selector">
        <div class="priority-label">{{langMap.prioritySetting[this.lang]}}(0~3):</div>
        <div class="priority-controls">
          <input
            type="number"
            v-model.number="newPriorityValue"
            class="priority-input"
            min="0"
            max="3"
            step="1"
          >
          <button @click="setLayerPriority" class="priority-apply-btn">{{langMap.apply[this.lang]}}</button>
        </div>
      </div>

      <!-- 现有的快捷操作按钮 -->
      <div class="panel-buttons">
        <button @click="moveLayerToTop">{{langMap.moveLayerToTop[this.lang]}}</button>
        <button @click="moveLayerUp">{{langMap.moveLayerUp[this.lang]}}</button>
        <button @click="moveLayerDown">{{langMap.moveLayerDown[this.lang]}}</button>
        <button @click="moveLayerToBottom">{{langMap.moveLayerToBottom[this.lang]}}</button>
      </div>

      <!-- 添加图层列表显示 -->
      <div class="layer-list-container" v-if="stationLayersList.length > 0">
        <div class="layer-list-title">{{langMap.currentStationLayers[this.lang]}}:</div>
        <div class="layer-list">
          <div
            v-for="(layer, index) in stationLayersList"
            :key="layer.tempId"
            class="layer-item"
            :class="{'current-layer': layer.tempId === currentFenceId}"
            @click="selectTargetLayer(layer.tempId)"
          >
            <span class="layer-priority">{{ layer.layerPriority || 0 }}</span>
            <span class="layer-name">{{ layer.name }}</span>
          </div>
        </div>
      </div>

      <div class="panel-close" @click="isShowLayerSortPanel = false">×</div>
    </div>

    <!-- 围栏工具按钮：对齐到地图缩放(+/-)控件右侧，避免遮挡 -->
    <div v-if="isEdit && !isDragModeActive && !hasUnsavedChanges && !(collectPreview && collectPreview.active && collectPreviewHasUnsavedChanges)" class="fence-tools-panel">
      <button
        @click="toggleFenceToolsMenu"
        :class="{'active': fenceToolsMenuOpen || isCutModeActive || isMergeModeActive || mergePreviewActive}"
        class="fence-tools-btn"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14.7 6.3a4.5 4.5 0 0 0-6.4 6.4l8 8a2 2 0 0 0 2.8 0l1.4-1.4a2 2 0 0 0 0-2.8z"></path>
          <path d="M6 12l-3 3 3 3 3-3"></path>
        </svg>
        {{ langMap.fenceTools[this.lang] }}
      </button>

      <div v-if="fenceToolsMenuOpen" class="fence-tools-menu">
        <!-- 切割：不做禁用态，点了再提示原因（例如未选中围栏） -->
        <button
          class="fence-tools-menu-item cut"
          @click="pickFenceTool('cut')"
        >
          <span class="fence-tools-menu-item-content">
            <span class="fence-tools-menu-icon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="2" y1="12" x2="22" y2="12"></line>
                <polyline points="7 5 2 12 7 19"></polyline>
                <polyline points="17 5 22 12 17 19"></polyline>
              </svg>
            </span>
            <span class="fence-tools-menu-text">{{ langMap.fenceCutting[this.lang] }}</span>
          </span>
        </button>
        <!-- 合并：仍保留禁用样式提示（但实际拦截逻辑在点击事件里统一处理） -->
        <button
          class="fence-tools-menu-item merge"
          @click="pickFenceTool('merge')"
          :class="{ 'is-disabled': !!getFenceToolDisabledReason('merge') }"
        >
          <span class="fence-tools-menu-item-content">
            <span class="fence-tools-menu-icon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M7 7h10v10H7z"></path>
                <path d="M9 9h10v10H9z"></path>
              </svg>
            </span>
            <span class="fence-tools-menu-text">{{ langMap.fenceMerging[this.lang] }}</span>
          </span>
        </button>
      </div>

      <div v-if="isCutModeActive" class="cut-mode-tips">
        {{ langMap.selectTwoCutPoints[this.lang] }}
        <button @click="cancelCutMode" class="cancel-cut-btn">{{ langMap.cancelCut[this.lang] }}</button>
      </div>

      <div v-if="isMergeModeActive" class="merge-mode-tips">
        <div class="merge-mode-message">
          {{ langMap.selectFencesToMerge[this.lang] }} ({{ mergeSelectedFenceIds.length }})
        </div>
        <div class="merge-mode-actions">
          <button @click="confirmMergeMode" class="merge-confirm-btn">{{ langMap.confirmMerge[this.lang] }}</button>
          <button @click="cancelMergeMode" class="merge-cancel-btn">{{ langMap.cancel[this.lang] }}</button>
        </div>
      </div>

      <div v-if="mergePreviewActive" class="merge-preview-tips">
        <div class="merge-preview-header">
          <div class="merge-preview-message">{{ langMap.mergePreviewReady[this.lang] }}</div>
          <button @click="cancelMergePreview" class="merge-cancel-preview-btn">{{ langMap.cancelMergePreview[this.lang] }}</button>
        </div>
        <div class="merge-preview-form">
          <div class="merge-preview-name-label">{{ langMap.newFenceName[this.lang] }}:</div>
          <input
            v-model="mergeNewFenceName"
            class="merge-preview-name-input"
            :placeholder="langMap.inputName[this.lang]"
            maxlength="50"
          >
        </div>
        <div class="merge-preview-actions">
          <button
            @click="saveMergePreview"
            class="merge-save-btn"
            :disabled="mergeSaving"
          >
            {{ mergeSaving ? langMap.saving[this.lang] : langMap.save[this.lang] }}
          </button>
        </div>
      </div>
    </div>

    <!-- 添加保存按钮面板 -->
    <div v-if="isEdit && hasUnsavedChanges" class="save-panel">
      <div class="save-panel-content">
        <div class="save-panel-message">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 20h9"></path>
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
          </svg>
          {{ langMap.hasUnsavedChanges[this.lang] }}
        </div>
        <div class="save-panel-buttons">
          <button @click="saveCurrentChanges" class="save-btn" :disabled="savingChanges">
            <svg v-if="savingChanges" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="loading-icon">
              <path d="M21 12a9 9 0 11-6.219-8.56"/>
            </svg>
            {{ savingChanges ? langMap.saving[this.lang] : langMap.save[this.lang] }}
          </button>
          <button @click="cancelCurrentChanges" class="cancel-btn" :disabled="savingChanges">
            {{ langMap.cancel[this.lang] }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="isEdit && collectPreview && collectPreview.active && collectPreviewHasUnsavedChanges" class="save-panel">
      <div class="save-panel-content">
        <div class="save-panel-message">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 20h9"></path>
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
          </svg>
          {{ langMap.hasUnsavedChanges[this.lang] }}
        </div>
        <div class="save-panel-buttons">
          <button @click="$emit('saveCollectPreview')" class="save-btn" :disabled="collectPreviewEditingLocked">
            {{ langMap.save[this.lang] }}
          </button>
          <button @click="cancelCollectPreviewEditsWithMask" class="cancel-btn" :disabled="collectPreviewEditingLocked">
            {{ langMap.cancel[this.lang] }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>
<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.pm";
import "leaflet.pm/dist/leaflet.pm.css";
import $ from "jquery";
import langMap from '@/utils/language';
import { mapState } from 'vuex';
import mapIntersectionUtil from '@/utils/mapIntersectionUtil';
import api from '@/api';
import ClipperLib from 'clipper-lib';

export default {
  name: "leafletUnit",
  props: {
    color: {
      type: Array,
      default: function () { return ['', '', '']; }
    },
    saveForm: {
      type: Object,
      default: function () {
        return {
          id: null,
          tempId: null,
          name: "",
          type: 1,
          typeStr: "",
          coordinateType: "WGS",
          area: null,
          description: ""
        }
      }
    },
    collectPreview: {
      type: Object,
      default: function () {
        return {
          active: false,
          id: '__collect_preview__',
          latlngs: []
        }
      }
    },
    collectPreviewEditingLocked: {
      type: Boolean,
      default: false
    },
    collectPreviewHasUnsavedChanges: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // 语言包映射
      langMap: langMap,
      // 默认地图类型（高德/通用）
      defaultMap: "",
      // 默认地图 URL（通用地图底图 URL）
      defaultMapUrl: "",
      // POI 搜索实例
      placeSearch: null,
      // Leaflet 地图实例
      map: null,
      // 围栏类型列表
      areaTypeList: [],
      // 围栏图层集合
      areaLayers: [],
      // 围栏编辑图层集合
      areaEditLayers: [],
      // 围栏数据列表
      areaList: [],
      // 当前操作的图层引用
      currentLayer: null,
      // 是否处于编辑模式
      isEdit: false,
      // 交叉区域图层集合
      intersectionLayers: [],
      // 当前选中的围栏 ID
      currentFenceId: null,
      // 是否显示图层排序面板
      isShowLayerSortPanel: false,
      // 待确认的图层操作类型
      pendingOperation: null,
      // 待提交的排序数据
      pendingSortData: null,
      // 各围栏原始形状快照（用于回滚）
      originalLayerShapes: {},
      // 当前正在编辑的图层引用
      currentEditingLayer: null,
      // 当前围栏原始坐标缓存
      originalLatLngs: null,
      // 优先级输入值
      newPriorityValue: 0,
      // 同基站围栏列表（用于面板展示）
      stationLayersList: [],
      // 目标围栏 ID（用于优先级参考）
      targetLayerId: null,
      // 是否处于切割模式
      isCutModeActive: false,
      // 切割点集合
      cutPoints: [],
      // 当前切割围栏 ID
      currentCutFenceId: null,
      // 切割模式顶点 marker 集合
      originalVertexMarkers: [],
      // 切割辅助线图层
      cutLineLayer: null,
      // 编辑模式提示控件
      editTipLayer: null,
      // 是否存在未保存更改
      hasUnsavedChanges: false,
      // 是否正在保存当前更改
      savingChanges: false,
      // 当前正在编辑的围栏 ID
      currentEditingFenceId: null,
      // 待保存的更改数据
      pendingChanges: null,
      // 拖拽节流定时器
      dragThrottleTimer: null,
      // 拖拽开始时的形状快照（用于回滚）
      currentDragStartShape: null,
      // 是否处于拖拽模式
      isDragModeActive: false,
      // 是否已绑定 popup 关闭监听
      popupCloseListenerAdded: false,
      // 采集预览图层
      collectPreviewLayer: null,
      // 采集预览原始坐标缓存
      collectPreviewOriginalLatLngs: null,
      // 是否处于合并模式
      isMergeModeActive: false,
      // 合并模式已选围栏 ID 列表
      mergeSelectedFenceIds: [],
      // 合并选择样式备份
      mergeSelectionStyleBackup: {},
      // 合并预览图层
      mergePreviewLayer: null,
      // 是否处于合并预览状态
      mergePreviewActive: false,
      // 合并预览来源围栏 ID 列表
      mergePreviewSourceIds: [],
      // 合并预览隐藏样式备份
      mergePreviewHiddenStyleBackup: {},
      // 合并预览坐标缓存
      mergePreviewLatLngs: [],
      // 合并后新围栏名称
      mergeNewFenceName: '',
      // 合并保存中状态
      mergeSaving: false,
      // 围栏工具下拉菜单是否展开
      fenceToolsMenuOpen: false,
    };
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
      mapConfig: state => state.app.leafletConfig
    })
  },
  watch: {
    // 监听未保存更改状态，通知父组件
    hasUnsavedChanges: {
      handler(newVal) {
        this.$emit('unsavedChangesChanged', !this.isEdit ? false : newVal);
      },
      immediate: true
    },
    collectPreview: {
      handler() {
        this.$nextTick(() => {
          this.renderCollectPreviewLayer();
          this.scheduleIntersectionsRecalc();
        });
      },
      deep: true
    },
    collectPreviewEditingLocked: {
      handler() {
        this.$nextTick(() => {
          this.applyCollectPreviewEditingState();
        });
      },
      immediate: true
    },
    collectPreviewHasUnsavedChanges: {
      handler() {
        this.$nextTick(() => {
          this.applyCollectPreviewEditingState();
        });
      },
      immediate: true
    }
  },
  mounted() {
    // 添加自定义样式来隐藏中间点
    const style = document.createElement('style');
    style.textContent = `
      .leaflet-marker-icon.marker-icon.marker-icon-middle.leaflet-zoom-animated.leaflet-interactive.leaflet-marker-draggable{
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  },
  methods: {
    //初始化leaflet地图
    async initMap(mapKey,mapUrl) {
      // 初始化 Leaflet 地图实例、底图与绘制控件，并绑定地图/绘制事件
      let _this = this;
      const tileLayerOption = this.mapConfig.tileLayerOption
      this.map = L.map("enclosure_map_id", this.mapConfig.mapInstantiated);
      L.tileLayer(tileLayerOption.url, tileLayerOption).addTo(this.map);

      // 添加地图点击事件，关闭图层操作面板
      this.map.on("click", e => {
        // 检查点击是否发生在图层操作面板内
        const isClickOnPanel = this.isClickOnElement(e.originalEvent, '.layer-sort-panel');
        if (!isClickOnPanel && this.isShowLayerSortPanel) {
          this.isShowLayerSortPanel = false;
        }
      });

      //添加绘制控制组件
      this.map.pm.setLang(this.lang || 'zh');
      this.map.pm.addControls({
        position: "topleft",
        drawPolygon: false, // 绘制多边形
        drawMarker: false, //绘制标记点
        drawCircleMarker: false, //绘制圆形标记
        drawPolyline: false, //绘制线条
        drawRectangle: false, //绘制矩形
        drawCircle: false, //绘制圆圈
        editMode: false, //编辑多边形
        dragMode: false, //拖动多边形 - 启用拖拽模式
        cutPolygon: false, // 添加一个按钮以删除多边形里面的部分内容
        removalMode: false // 清除多边形
      });

      //监听区域绘制完毕
      this.map.on("pm:create", e => {
        if (e.shape === "Polygon") {
          const layerObj = e.layer;

          // 添加图层
          _this.areaLayers.push(layerObj);

          // 更新区域并触发保存
            _this.updateArea(layerObj);

          //点击弹出信息框
            layerObj.on("click", e => {
              _this.createPopup(e.target.options.id, e.latlng);
            });

          //清除绘制
          _this.map.pm.disableDraw("Polygon");

          // 重新计算并显示交叉区域
          _this.$nextTick(() => {
            _this.findAndDisplayIntersections();
          });
        }
      });
    },
    moveToCenter(point) {
      // 将地图平移到指定经纬度点
      this.map.panTo([point[1], point[0]]);
    },
    emitAreasRendered() {
      // 通知父组件地图围栏图层已完成渲染（双 RAF 保证 DOM/Leaflet 渲染稳定）
      const raf = (typeof window !== 'undefined' && window.requestAnimationFrame) ? window.requestAnimationFrame : (fn) => setTimeout(fn, 0);
      this.$nextTick(() => {
        raf(() => {
          raf(() => {
            this.$emit('areasRendered');
          });
        });
      });
    },
    scheduleIntersectionsRecalc() {
      // 调度交叉区域重算（延迟到下一帧/宏任务，避免频繁计算阻塞交互）
      const raf = (typeof window !== 'undefined' && window.requestAnimationFrame) ? window.requestAnimationFrame : (fn) => setTimeout(fn, 0);
      this.$nextTick(() => {
        raf(() => {
          setTimeout(() => {
            this.findAndDisplayIntersections();
            this.emitAreasRendered();
          }, 0);
        });
      });
    },
    //加载现有区域
    loadAreas(areaList,currentFenceId) {
      // 根据围栏数据渲染地图图层，并恢复编辑态/聚焦选中围栏
      // 清除现有图层
      this.areaLayers = [];
      this.areaList = areaList;
      // 记录当前编辑的电子围栏ID
      this.currentFenceId = currentFenceId
      this.clearCollectPreviewLayer();
      this.cancelMergeMode();
      this.cancelMergePreview(false);
      // 移除所有非底图图层
      this.map.eachLayer(function (layer) {
        if (
          !layer._container ||
          ("" + $(layer._container).attr("class")).replace(/\s/g, "") !=
          "leaflet-layer"
        ) {
          layer.remove();
        }
      });

      // 使用mapIntersectionUtil中的排序方法
      const sortedAreaList = mapIntersectionUtil.sortAreasByPriority(this.areaList, false);

      // 按照排序后的顺序绘制区域
      sortedAreaList.forEach((item) => {
        if (item.area !== null) {
          // 创建多边形
          let polygon = this.createFencePolygon(item);
          if (polygon) {
            polygon.addTo(this.map);

          let _this = this;
          //弹出信息层
          polygon.on("click", e => {
            const didHandle = _this.createPopup(item.tempId, e.latlng);
            if (didHandle) {
              _this.currentFenceId = item.tempId; // 记录当前点击的围栏ID
            }
          });

          this.areaLayers.push(polygon);
          }
        }
      });

      this.renderCollectPreviewLayer();

      // 计算并显示交叉区域
      this.findAndDisplayIntersections();

      // 若是编辑模式则重新监听编辑
      if (this.isEdit){
        this.onEdit();
        // 如果有记录的当前围栏ID，则聚焦到该围栏
        if (this.currentFenceId) {
          this.$nextTick(() => {
            this.focusOnFence(this.currentFenceId);
            this.createPopup(this.currentFenceId);
            this.emitAreasRendered();
          });
          return;
        }else{
          //当前编辑模式下初始化都置透明不可编辑
          this.disableAllFencesEdit()
        }
      }
      this.emitAreasRendered();
    },
    renderCollectPreviewLayer() {
      // 渲染“采集预览”围栏图层（与普通围栏分离，便于编辑/回滚）
      if (!this.map) return;
      if (!this.collectPreview || !this.collectPreview.active) {
        this.clearCollectPreviewLayer();
        return;
      }
      const id = this.collectPreview.id || '__collect_preview__';
      const latlngs = Array.isArray(this.collectPreview.latlngs) ? this.collectPreview.latlngs : [];
      if (latlngs.length < 3) {
        this.clearCollectPreviewLayer();
        return;
      }

      const normalized = latlngs.map(p => ({ lat: parseFloat(p.lat), lng: parseFloat(p.lng) }))
        .filter(p => !Number.isNaN(p.lat) && !Number.isNaN(p.lng));
      if (normalized.length < 3) {
        this.clearCollectPreviewLayer();
        return;
      }

      if (!this.collectPreviewLayer) {
        this.collectPreviewLayer = L.polygon(normalized, {
          color: '#ff9900',
          dashArray: '6 6',
          weight: 2,
          fillColor: '#ff9900',
          fillOpacity: 0.15,
          id
        }).addTo(this.map);
        this.collectPreviewLayer.bringToFront();
        this.bindCollectPreviewEvents(this.collectPreviewLayer);
      } else {
        this.collectPreviewLayer.setLatLngs(normalized);
        this.collectPreviewLayer.bringToFront();
      }
      this.collectPreviewOriginalLatLngs = JSON.parse(JSON.stringify(this.collectPreviewLayer._latlngs[0] || []));
      this.applyCollectPreviewEditingState();
    },
    clearCollectPreviewLayer() {
      // 移除采集预览图层并重置相关缓存
      if (this.collectPreviewLayer && this.map) {
        this.collectPreviewLayer.off();
        this.map.removeLayer(this.collectPreviewLayer);
      }
      this.collectPreviewLayer = null;
      this.collectPreviewOriginalLatLngs = null;
    },
    applyCollectPreviewEditingState() {
      // 根据当前选中与锁定状态，启用/禁用采集预览的编辑与拖拽
      if (!this.collectPreviewLayer || !this.collectPreviewLayer.pm) return;
      const collectId = this.collectPreview && this.collectPreview.id ? this.collectPreview.id : '__collect_preview__';
      const isSelected = this.currentFenceId === collectId;
      if (this.collectPreviewEditingLocked || !this.isEdit || !isSelected) {
        this.collectPreviewLayer.pm.disable();
        if (this.collectPreviewLayer.pm.disableLayerDrag) {
          this.collectPreviewLayer.pm.disableLayerDrag();
        }
        return;
      }
      this.collectPreviewLayer.pm.enable({ allowSelfIntersection: false });
      if (this.collectPreviewLayer.pm.enableLayerDrag) {
        this.collectPreviewLayer.pm.enableLayerDrag();
      }
    },
    bindCollectPreviewEvents(layer) {
      // 绑定采集预览图层的编辑/拖拽/点击事件，用于快照、回传与聚焦
      const startSnapshot = () => {
        this.collectPreviewOriginalLatLngs = JSON.parse(JSON.stringify((layer._latlngs && layer._latlngs[0]) || []));
      };
      layer.on('pm:editstart', startSnapshot);
      layer.on('pm:dragstart', startSnapshot);
      layer.on('click', () => {
        this.selectCollectPreview(false);
      });
      layer.on('pm:vertexadded', () => this.emitCollectPreviewEdited(layer));
      layer.on('pm:vertexremoved', () => this.emitCollectPreviewEdited(layer));
      layer.on('pm:markerdragend', () => {
        this.$emit('areasRenderStart');
        this.emitCollectPreviewEdited(layer);
      });
      layer.on('pm:dragend', () => {
        this.$emit('areasRenderStart');
        this.emitCollectPreviewEdited(layer);
      });
    },
    emitCollectPreviewEdited(layer) {
      // 对比采集预览的新旧坐标并通知父组件，同时触发交叉区域重算
      const current = ((layer._latlngs && layer._latlngs[0]) || []).map(p => ({ lat: p.lat, lng: p.lng }));
      const old = (this.collectPreviewOriginalLatLngs || []).map(p => ({ lat: p.lat, lng: p.lng }));
      if (current.length >= 3 && old.length >= 3) {
        this.$emit('collectPreviewEdited', { latlngs: current, oldLatlngs: old });
      }
      this.collectPreviewOriginalLatLngs = JSON.parse(JSON.stringify((layer._latlngs && layer._latlngs[0]) || []));
      this.scheduleIntersectionsRecalc();
    },
    restoreCollectPreviewLatLngs(latlngs) {
      // 将采集预览图层恢复到指定坐标（用于取消编辑/回滚）
      if (!this.collectPreviewLayer) return;
      if (!Array.isArray(latlngs) || latlngs.length < 3) return;
      const normalized = latlngs.map(p => ({ lat: parseFloat(p.lat), lng: parseFloat(p.lng) }))
        .filter(p => !Number.isNaN(p.lat) && !Number.isNaN(p.lng));
      if (normalized.length < 3) return;
      this.collectPreviewLayer.setLatLngs(normalized);
      this.collectPreviewLayer.redraw && this.collectPreviewLayer.redraw();
      this.collectPreviewOriginalLatLngs = JSON.parse(JSON.stringify(this.collectPreviewLayer._latlngs[0] || []));
      this.scheduleIntersectionsRecalc();
    },
    focusOnCollectPreview() {
      // 缩放并聚焦到采集预览围栏的 bounds
      if (!this.collectPreviewLayer) return;
      const bounds = this.collectPreviewLayer.getBounds();
      this.map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 29,
        animate: true
      });
    },
    //更新区域 - 修改为不自动保存，而是标记为有未保存的更改
    updateArea(layer) {
      // 把编辑后的围栏坐标写回本地列表，并标记未保存（由用户手动保存/取消）
      // 记录当前编辑的电子围栏ID
      this.currentFenceId = layer.options.id;
      this.currentEditingFenceId = layer.options.id;

      // 保存原始形状，以便在保存失败时恢复
      const originalShape = JSON.parse(JSON.stringify(layer._latlngs[0]));

      this.areaList.forEach((item, index, arr) => {
        if (
          layer &&
          layer.options &&
          layer.options.id &&
          layer.options.id === item.tempId
        ) {
          if (layer._latlngs[0]) {
            let areas = [];
            layer._latlngs[0].forEach((item1, index1, arr1) => {
              let type;
              if (this.saveForm.coordinateType === "WGS") {
                type = 0;
              } else if (
                this.saveForm.coordinateType === "PLI" ||
                this.saveForm.coordinateType === "PLO"
              ) {
                type = 1;
              }
              let point = {
                lon: item1.lng,
                lat: item1.lat,
                type: type
              };
              areas[index1] = point;
            });
            let area = JSON.stringify(areas);

            // 不再自动保存，而是保存待保存的更改
            const updatedItem = { ...item, area: area };
            this.pendingChanges = updatedItem;
            this.hasUnsavedChanges = true;

            // 禁用其他围栏的点击
            this.disableOtherFencesInteraction();

            // 更新本地数据用于显示
            this.$set(this.areaList, index, updatedItem);
            // 父组件更新列表数据
            this.$emit('updateAreaList', this.areaList);
          }
        }
      });
    },
    //绘制区域
    drawFence(row) {
      // 开启绘制新围栏的多边形模式，并按围栏类型设置颜色与绘制参数
      let color = this.color[row.type];

      let currentLayer = this.map.pm.enableDraw("Polygon", {
        templineStyle: { color: color },
        hintlineStyle: { color: color, dashArray: [5, 5] },
        pathOptions: {
          color: color,
          fillColor: color,       // 直接使用原始颜色
          fillOpacity: 0.5,       // 半透明填充
          weight: 2,              // 边框宽度适中
          id: row.tempId
        },
        snappable: true,
        snapDistance: 20,
        allowSelfIntersection: false,
        finishOn: "dblclick"
      });
    },
    // 监听编辑
    onEdit() {
      // 为当前所有围栏图层绑定编辑/拖拽等事件监听
      let _this = this;

      // 先确保清除所有图层上的事件监听
      this.areaLayers.forEach((item) => {
        this.clearLayerEvents(item);
      });

      // 为每个图层添加事件监听
      this.areaLayers.forEach((item) => {
        // 立即保存原始形状，不依赖于事件
        this.saveOriginalShape(item);

        // 添加编辑模式切换事件监听（更可靠）
        item.on("pm:editmodetoggled", e => this.handleEditModeToggled(e, item));

        // 保留原有的 editstart 事件，双重保险
        item.on("pm:editstart", e => this.handleEditStart(e, item));

        // 添加各种编辑事件监听
        item.on("pm:vertexadded", e => this.handleEditEvent(e, "vertexadded"));
        item.on("pm:vertexremoved", e => this.handleEditEvent(e, "vertexremoved"));
        item.on("pm:edit", e => this.handleEditEvent(e, "edit"));

        // 只在拖拽结束时更新区域，并检查冲突
        item.on("pm:markerdragend", e => this.handleDragEnd(e));
      });

      // 确保交叉区域不可编辑
      this.makeIntersectionLayersNonEditable();
    },
    // 清除图层上的所有事件监听
    clearLayerEvents(layer) {
      // 清理单个图层上绑定的 pm 编辑/拖拽事件，避免重复触发
      layer.off("pm:edit");
      layer.off("pm:markerdragend");
      layer.off("pm:vertexadded");
      layer.off("pm:vertexremoved");
      layer.off("pm:editstart");
      layer.off("pm:editmodetoggled");
      // 清除拖拽事件
      layer.off("pm:dragstart");
      layer.off("pm:drag");
      layer.off("pm:dragend");
    },
    // 保存图层的原始形状
    saveOriginalShape(layer) {
      // 保存围栏原始坐标快照，用于冲突处理/取消编辑回滚
      if (layer && layer._latlngs && layer._latlngs[0] && layer.options && layer.options.id) {
        const layerId = layer.options.id;

        // 只有当这个图层还没有保存过原始形状时才保存
        if (!this.originalLayerShapes[layerId]) {
          this.originalLayerShapes[layerId] = JSON.parse(JSON.stringify(layer._latlngs[0]));
        }

        // 如果当前正在编辑这个图层，也更新 originalLatLngs
        if (this.currentFenceId === layerId) {
          this.originalLatLngs = this.originalLayerShapes[layerId];
        }
      }
    },

    // 强制更新原始形状（用于保存成功后更新基准点）
    updateOriginalShape(layer) {
      // 保存成功后，更新原始形状基准（后续回滚以此为准）
      if (layer && layer._latlngs && layer._latlngs[0] && layer.options && layer.options.id) {
        const layerId = layer.options.id;
        this.originalLayerShapes[layerId] = JSON.parse(JSON.stringify(layer._latlngs[0]));

        // 如果当前正在编辑这个图层，也更新 originalLatLngs
        if (this.currentFenceId === layerId) {
          this.originalLatLngs = this.originalLayerShapes[layerId];
        }
      }
    },
    // 处理编辑模式切换事件
    handleEditModeToggled(e, layer) {
      // 进入编辑模式时记录当前编辑图层并确保原始形状已备份
      if (e.enabled) { // 当编辑模式开启时
        this.currentEditingLayer = e.layer || layer;
        this.currentFenceId = (e.layer && e.layer.options.id) || layer.options.id;

        // 确保我们有原始形状的备份
        const targetLayer = e.layer || layer;
        this.saveOriginalShape(targetLayer);
      }
    },
    // 处理编辑开始事件
    handleEditStart(e, layer) {
      // 开始编辑时记录当前编辑图层与围栏 ID，并备份原始形状
      this.currentEditingLayer = e.layer || e.target || layer;
      this.currentFenceId = (e.layer && e.layer.options.id) ||
                          (e.target && e.target.options.id) ||
                          layer.options.id;

      // 确保我们有原始形状的备份
      const targetLayer = e.layer || e.target || layer;
      this.saveOriginalShape(targetLayer);
    },
    // 通用的编辑事件处理方法 - 使用统一冲突处理
    handleEditEvent(e, eventType) {
      // 处理顶点增删/编辑等事件，并检测与同优先级围栏冲突
          // 获取当前图层ID
          const layerId = e.target.options.id;

          // 如果没有原始形状，尝试从字典中获取
      if (!this.originalLatLngs && this.originalLayerShapes[layerId]) {
        this.originalLatLngs = this.originalLayerShapes[layerId];
          }

          // 检查是否会导致交叉
      const hasConflict = this.checkEditingConflict(e.target);
          if (hasConflict) {
        this.handleConflict(e.target, layerId);
      }
    },
    // 处理拖拽结束事件 - 使用统一冲突处理
    handleDragEnd(e) {
      // 拖拽结束后更新图形并做冲突检测（冲突则回滚）
      this.$emit('areasRenderStart');
          // 记录当前编辑的电子围栏ID
      this.currentFenceId = e.target.options.id;
          const layerId = e.target.options.id;

          // 如果没有原始形状，尝试从字典中获取
      if (!this.originalLatLngs && this.originalLayerShapes[layerId]) {
        this.originalLatLngs = this.originalLayerShapes[layerId];
          }

          // 检查是否与同优先级图层产生交叉
      const hasConflict = this.checkEditingConflict(e.target);
          if (hasConflict) {
        this.handleConflict(e.target, layerId);
        this.scheduleIntersectionsRecalc();
          } else {
            // 没有冲突，更新图形
        this.updateArea(e.target);
        this.scheduleIntersectionsRecalc();
          }
    },
      // 确保交叉区域不可编辑
    makeIntersectionLayersNonEditable() {
      // 将交叉区域图层设为不可编辑（禁止拖拽/编辑点）
      if (this.intersectionLayers && this.intersectionLayers.length > 0) {
        this.intersectionLayers.forEach(layer => {
          mapIntersectionUtil.makeLayerNonEditable(layer);
        });
      }
    },
    /**
     * 切换地图围栏编辑模式。
     * - 进入编辑：初始化单图层编辑态（禁用其他图层编辑、保留轮廓、提示用户选择围栏）
     * - 退出编辑：如果存在采集预览草稿或未保存编辑，弹出确认；确认后自动清理采集预览并退出
     */
    editFence(isEdit) {
      // 切换编辑模式；退出时处理未保存更改/采集预览/合并预览的确认与清理
      if (!isEdit) {
        const hasCollectPreviewDraft = !!(this.collectPreview && this.collectPreview.active);
        // 合并预览也视为“未保存草稿”，退出编辑时需要二次确认并清理预览紫色线条/恢复原围栏
        const hasMergePreviewDraft = !!this.mergePreviewActive;
        const hasAnyUnsavedChanges = !!(this.hasUnsavedChanges || hasCollectPreviewDraft || hasMergePreviewDraft);

        const doExit = () => {
          // 确认退出编辑后，统一清理合并预览，避免残留紫色预览线条
          if (this.mergePreviewActive) {
            this.cancelMergePreview(false, false);
          }
          this.cancelMergeMode();
          this.isEdit = false;
          this.applyCollectPreviewEditingState();

          if (this.isCutModeActive) {
            this.cancelCutMode();
          }

          if (hasCollectPreviewDraft) {
            this.cancelCollectPreviewWithMask(false);
          }

          this.exitEditMode();
        };

        if (hasAnyUnsavedChanges) {
          this.$Modal.confirm({
            title: langMap.confirmOperation[this.lang],
            content: langMap.hasUnsavedChangesConfirm[this.lang],
            onOk: () => {
              doExit();
            }
          });
          return;
        }

        doExit();
        return;
      }

      this.isEdit = true;
      this.applyCollectPreviewEditingState();

      // 进入编辑模式
      // 保存所有图层的原始形状，用于冲突时恢复
      this.originalLayerShapes = {};
      this.areaLayers.forEach(layer => {
        this.saveOriginalShape(layer);

        // 先禁用所有图层的编辑模式
        if (layer && layer.pm) {
          layer.pm.disable();
        }

        // 隐藏所有顶点，但保持轮廓可见
        if (layer._path) {
          layer._path.style.opacity = '0.5';
        }
      });

      // 显示提示信息
      this.showEditModeMessage();

      // 关闭现有的弹出窗口
      this.map.closePopup();

      // 添加CSS样式，确保交叉区域不可编辑
      mapIntersectionUtil.addNonEditableStyle();

      // 切换到单图层编辑模式
      this.currentFenceId = null;
    },

    // 添加显示编辑模式提示的方法
    showEditModeMessage() {
      // 在地图上方显示“请选择围栏进行编辑”的提示控件
      // 先移除之前的提示（如果有）
      this.removeEditTipLayer();

      // 创建一个自定义HTML元素作为提示
      const container = L.DomUtil.create('div', 'edit-mode-tip');
      container.innerHTML = `
        <div class="edit-mode-tip-content">
          <div class="edit-mode-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 20h9"></path>
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
            </svg>
          </div>
          <div class="edit-mode-message">${this.langMap.selectFenceToEdit[this.lang]}</div>
        </div>
      `;

      // 创建自定义控件并添加到地图
      this.editTipLayer = L.control({ position: 'topright' });
      this.editTipLayer.onAdd = function() {
        return container;
      };
      this.editTipLayer.addTo(this.map);
    },

    // 添加一个专门的方法来移除提示层
    removeEditTipLayer() {
      // 移除编辑模式提示控件
      if (this.editTipLayer) {
        try {
          this.map.removeControl(this.editTipLayer);
        } catch (e) {
          this.$Notice.error({
            title: langMap.tips[this.lang],
            desc: langMap.removeTipLayerError[this.lang] + ': ' + (e.message || langMap.unknown[this.lang])
          });
        }
        this.editTipLayer = null;
      }
    },

    // 重写围栏点击事件处理，以适应新的编辑模式
    createPopup(tempId, clickLatLng) {
      // 处理围栏点击入口（合并选择/切割拦截/编辑激活/弹窗展示等）
      if (this.isMergeModeActive) {
        this.toggleMergeFenceSelection(tempId);
        return false;
      }
      // 如果正在切割模式，禁用围栏点击
      if (this.isCutModeActive) {
        this.$Notice.warning({
          title: langMap.tip[this.lang],
          desc: langMap.cutModeActiveCannotClick[this.lang] || '切割模式下无法点击其他围栏'
        });
        return false;
      }

      // 如果点击了不同的围栏，且面板正在显示，则关闭面板
      if (this.currentFenceId !== tempId && this.isShowLayerSortPanel) {
        this.isShowLayerSortPanel = false;
      }
      // 在编辑模式下，点击围栏会激活它的编辑功能
      if (this.isEdit) {
        const collectId = this.collectPreview && this.collectPreview.id ? this.collectPreview.id : '__collect_preview__';
        // 检查是否有未保存的更改（硬拦截切换）
        if (this.hasUnsavedChanges && this.currentEditingFenceId !== tempId) {
          this.$Notice.warning({
            title: langMap.tip[this.lang],
            desc: langMap.pleaseFinishCurrentEdit[this.lang]
          });
          return false;
        }
        if (this.collectPreviewHasUnsavedChanges && this.currentFenceId === collectId && tempId !== collectId) {
          this.$Notice.warning({
            title: langMap.tip[this.lang],
            desc: langMap.pleaseFinishCurrentEdit[this.lang]
          });
          return false;
        }
        this.activateFenceEdit(tempId);
        return true;
      }

      // 以下是非编辑模式下的正常弹出窗口逻辑
      // 在非编辑模式下也要通知父组件围栏被选中
      this.$emit('activeFenceChanged', tempId);
      
      let node = this.findFenceById(tempId);
      if (!node) return false;

      let layer = this.findLayerById(tempId);
      if (!layer) return false;

      let typeStr = node.typeStr ? node.typeStr : "";

      // 添加图层排序按钮
      let sortButton = '';
      if (node.stationId) {
        sortButton = `<button class="sort-layer-btn" onclick="window.openLayerSortPanel(${tempId})">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 5px;">
            <line x1="21" y1="10" x2="3" y2="10"></line>
            <line x1="21" y1="6" x2="3" y2="6"></line>
            <line x1="21" y1="14" x2="3" y2="14"></line>
            <line x1="21" y1="18" x2="3" y2="18"></line>
          </svg>
          ${langMap.layerSort[this.lang]}
        </button>`;
      }

      // 获取优先级值，确保显示为数字
      const layerPriority = node.layerPriority !== undefined ? node.layerPriority : 0;

      let content =
        '<div class="input-card content-window-card"><div style="padding:7px 0px 0px 0px;">' +
        "<h4>" + langMap.name[this.lang] + "：" + node.name + "</h4>" +
        '<p class="input-item">' + langMap.type[this.lang] + "：" + typeStr + "</p>" +
        '<p class="input-item">' + langMap.description[this.lang] + "：" + node.description + "</p>" +
        '<p class="input-item">' + langMap.belongStation[this.lang] + "：" + (node.stationName || '') + "</p>" +
        // 添加优先级显示行
        '<p class="input-item">' + (langMap.layerPriority[this.lang] || '图层优先级') + "：" + layerPriority + "</p>" +
        sortButton +
        "</div></div>";

      if (this.map && (this.map._animatingZoom || (this.map._panAnim && this.map._panAnim._inProgress))) {
        this.map.once('moveend', () => {
          this.createPopup(tempId, clickLatLng);
        });
        return true;
      }

      let popupLatLng = null;
      if (clickLatLng && typeof clickLatLng.lat === 'number' && typeof clickLatLng.lng === 'number') {
        popupLatLng = clickLatLng;
      } else if (layer.getBounds) {
        const boundsCenter = layer.getBounds().getCenter();
        const latLngs = layer.getLatLngs ? layer.getLatLngs() : null;
        const ring = Array.isArray(latLngs) && latLngs.length ? latLngs[0] : null;
        const points = Array.isArray(ring) ? ring : null;

        const pointInPolygon = (pt, poly) => {
          let inside = false;
          for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
            const xi = poly[i].lng, yi = poly[i].lat;
            const xj = poly[j].lng, yj = poly[j].lat;
            const intersect = ((yi > pt.lat) !== (yj > pt.lat)) &&
              (pt.lng < (xj - xi) * (pt.lat - yi) / (yj - yi + 0.0) + xi);
            if (intersect) inside = !inside;
          }
          return inside;
        };

        const polygonCentroid = (poly) => {
          let area = 0;
          let cx = 0;
          let cy = 0;
          for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
            const x0 = poly[j].lng, y0 = poly[j].lat;
            const x1 = poly[i].lng, y1 = poly[i].lat;
            const a = x0 * y1 - x1 * y0;
            area += a;
            cx += (x0 + x1) * a;
            cy += (y0 + y1) * a;
          }
          area *= 0.5;
          if (!area) return null;
          cx /= (6 * area);
          cy /= (6 * area);
          if (!isFinite(cx) || !isFinite(cy)) return null;
          return L.latLng(cy, cx);
        };

        if (points && points.length >= 3) {
          if (pointInPolygon(boundsCenter, points)) {
            popupLatLng = boundsCenter;
          } else {
            const c = polygonCentroid(points);
            if (c && pointInPolygon(c, points)) {
              popupLatLng = c;
            } else {
              popupLatLng = points[0];
            }
          }
        } else {
          popupLatLng = boundsCenter;
        }
      } else if (layer._bounds && layer._bounds.getCenter) {
        popupLatLng = layer._bounds.getCenter();
      } else if (layer._bounds && layer._bounds._northEast && layer._bounds._southWest) {
        const lat =
          (layer._bounds._northEast.lat - layer._bounds._southWest.lat) / 2 +
          layer._bounds._southWest.lat;
        const lng =
          (layer._bounds._northEast.lng - layer._bounds._southWest.lng) / 2 +
          layer._bounds._southWest.lng;
        popupLatLng = L.latLng(lat, lng);
      } else {
        return false;
      }

      if (this.map && this.map.invalidateSize) {
        this.map.invalidateSize({ pan: false });
      }

      let popup = L.popup({
        offset: [0, 0],
        autoPan: true,
        autoPanPaddingTopLeft: [40, 40],
        autoPanPaddingBottomRight: [40, 40]
      })
        .setLatLng(popupLatLng)
        .setContent(content);

      // 在地图上添加popup关闭事件监听
      if (!this.popupCloseListenerAdded) {
        this.map.on('popupclose', ()=> {
          if (this.isShowLayerSortPanel) {
            this.isShowLayerSortPanel = false;
          }
        });
        this.popupCloseListenerAdded = true;
      }

      popup.openOn(this.map);
      if (this.map && this.map.panInside) {
        this.map.panInside(popupLatLng, {
          paddingTopLeft: [40, 40],
          paddingBottomRight: [40, 40]
        });
      }

      // 添加全局方法用于从popup中调用
      window.openLayerSortPanel = (id)=> {
        this.openLayerSortPanel(id);
      };
      return true;
    },

    // 根据ID查找围栏
    findFenceById(id) {
      // 从围栏列表中按 tempId 查找围栏数据
      let targetFence = null;
      this.areaList.forEach(item => {
        if (item.tempId === id) {
          targetFence = item;
        }
      });
      return targetFence;
    },

    // 根据ID查找图层
    findLayerById(id) {
      // 从地图图层集合中按 options.id 查找围栏图层
      let targetLayer = null;
      this.areaLayers.forEach(layer => {
        if (layer.options.id === id) {
          targetLayer = layer;
        }
      });
      return targetLayer;
    },
    findLayerByIdLoose(id) {
      // 宽松匹配方式查找围栏图层（兼容字符串/数字 id）
      const key = id != null ? String(id) : '';
      let targetLayer = null;
      this.areaLayers.forEach(layer => {
        if (!layer || !layer.options) return;
        if (String(layer.options.id) === key) {
          targetLayer = layer;
        }
      });
      return targetLayer;
    },

    // 创建围栏多边形
    createFencePolygon(item) {
      // 根据围栏数据创建 Leaflet Polygon 图层并设置样式/元数据
      if (item.area === null) return null;

      let color = this.color[item.type];
      let areas = JSON.parse(item.area);
      let areaTemp = [];
      areas.forEach((item1, index1) => {
        let point = [item1.lat, item1.lon];
        areaTemp[index1] = point;
      });

      // 创建多边形，使用对应的颜色和图案
      let polygon = L.polygon(areaTemp, {
        color: color,           // 边框颜色
        fillColor: color,       // 填充颜色
        fillOpacity: 0.5,       // 半透明填充
        weight: 2,              // 边框宽度
        id: item.tempId,
        type: item.type,
        stationId: item.stationId,
        layerPriority: item.layerPriority || 0, // 使用layerPriority字段，默认为0
        createTime: item.createTime
      });

      return polygon;
    },

    // 获取当前选中围栏的信息
    getCurrentFenceInfo() {
      // 获取当前选中的围栏数据
      return this.findFenceById(this.currentFenceId);
    },

    // 获取同一基站下的所有围栏
    getLayersInSameStation(stationId) {
      // 获取同基站下所有围栏（用于排序/冲突检测）
      if (!stationId) return [];

      return this.areaList.filter(item => item.stationId === stationId);
    },

    // 围栏操作前置检查（检查当前围栏是否可以进行操作）
    checkFenceOperationAvailable(checkTopOrBottom = false, isTop = true) {
      // 校验当前围栏是否满足可操作条件，并返回围栏/同基站列表等上下文
      const currentFence = this.getCurrentFenceInfo();
      if (!currentFence || !currentFence.stationId) {
        this.$Message.info(langMap.pleaseSelectValidFence[this.lang]);
        return { available: false };
      }

      // 当前围栏的优先级
      const currentPriority = currentFence.layerPriority || 0;

      // 检查是否已经在最高或最低优先级
      if (checkTopOrBottom) {
        if (isTop) {
          // 如果当前图层已经是最高层(3)，则不操作
          if (currentPriority >= 3) {
            this.$Notice.info({
              title: langMap.tip[this.lang],
              desc: langMap.layerAlreadyTop[this.lang]
            });
            return { available: false };
          }
        } else {
          // 如果当前图层已经是最底层(0)，则不操作
          if (currentPriority <= 0) {
            this.$Notice.info({
              title: langMap.tip[this.lang],
              desc: langMap.layerAlreadyBottom[this.lang]
            });
            return { available: false };
          }
        }
      }

      // 获取同一基站下的所有围栏
      const stationLayers = this.getLayersInSameStation(currentFence.stationId);

      return {
        available: true,
        currentFence,
        stationLayers
      };
    },

    // 检查两个图层是否有交叉或边界线重叠
    checkLayersIntersection(layer1, layer2) {
      // 检测两个围栏图层是否存在交叉（用于冲突判断）
      try {
        if (!this.areValidLayers(layer1, layer2)) return false;

        // 获取两个多边形的坐标
        const poly1 = layer1._latlngs[0];
        const poly2 = layer2._latlngs[0];

        // 只检查多边形区域是否相交，移除边界线重叠检测
        return this.checkPolygonIntersection(poly1, poly2);
      } catch (e) {
        this.$Notice.error({
          title: langMap.tip[this.lang],
          desc: langMap.checkIntersectionError[this.lang] + ': ' + (e.message || langMap.unknown[this.lang])
        });
        return false;
      }
    },

    // 保持 checkPolygonIntersection 方法不变
    checkPolygonIntersection(poly1, poly2) {
      // 计算两个多边形是否相交（返回是否存在交集）
      const intersection = mapIntersectionUtil.calculateIntersection(poly1, poly2);
      return intersection && intersection.length > 0;
    },

    // 检查图层是否有效的方法保持不变
    areValidLayers(layer1, layer2) {
      // 校验图层对象是否具备有效坐标数据
      return layer1 && layer2 && layer1._latlngs && layer2._latlngs;
    },
    // 检查两个多边形的边界线是否有重叠(用不到先保留)
    checkBoundaryOverlap(poly1, poly2) {
      // 检查两个多边形边界线段是否存在重叠（预留能力）
      // 容差值，用于处理浮点数精度问题
      const EPSILON = 1e-9;

      // 检查线段是否重叠
      function checkLineSegmentsOverlap(line1Start, line1End, line2Start, line2End) {
        // 检查两条线段是否共线
        function arePointsCollinear(p1, p2, p3) {
          // 计算叉积，如果接近0，则三点共线
          const crossProduct =
            (p2.lng - p1.lng) * (p3.lat - p1.lat) -
            (p2.lat - p1.lat) * (p3.lng - p1.lng);
          return Math.abs(crossProduct) < EPSILON;
        }

        // 检查点是否在线段上
        function isPointOnSegment(p, segStart, segEnd) {
          return p.lng >= Math.min(segStart.lng, segEnd.lng) - EPSILON &&
                 p.lng <= Math.max(segStart.lng, segEnd.lng) + EPSILON &&
                 p.lat >= Math.min(segStart.lat, segEnd.lat) - EPSILON &&
                 p.lat <= Math.max(segStart.lat, segEnd.lat) + EPSILON &&
                 arePointsCollinear(p, segStart, segEnd);
        }

        // 如果线段不共线，则不可能重叠
        if (!arePointsCollinear(line1Start, line1End, line2Start) ||
            !arePointsCollinear(line1Start, line1End, line2End)) {
          return false;
        }

        // 检查是否有端点在另一条线段上
        return isPointOnSegment(line1Start, line2Start, line2End) ||
               isPointOnSegment(line1End, line2Start, line2End) ||
               isPointOnSegment(line2Start, line1Start, line1End) ||
               isPointOnSegment(line2End, line1Start, line1End);
      }

      // 检查所有边界线段
      for (let i = 0; i < poly1.length; i++) {
        const p1Start = poly1[i];
        const p1End = poly1[(i + 1) % poly1.length]; // 循环到第一个点

        for (let j = 0; j < poly2.length; j++) {
          const p2Start = poly2[j];
          const p2End = poly2[(j + 1) % poly2.length]; // 循环到第一个点

          if (checkLineSegmentsOverlap(p1Start, p1End, p2Start, p2End)) {
            return true; // 发现边界线重叠
          }
        }
      }

      return false; // 没有边界线重叠
    },
    // 计算新的排序数据 - 更新版本，限制最高优先级为3
    calculateSortData(operation, currentFence, stationLayers) {
      // 根据置顶/置底/上移/下移计算新的 layerPriority 并做冲突校验
      // 获取ID
      const id = currentFence.id || currentFence.tempId;

      // 当前优先级
      const currentPriority = currentFence.layerPriority || 0;

      let newPriority;

      // 根据操作类型计算新的优先级值
      switch (operation) {
        case 'moveToTop':
          // 限制最高优先级为3
          newPriority = 3;
          break;
        case 'moveToBottom':
          // 最低优先级为0
          newPriority = 0;
          break;
        case 'moveUp': {
          // 上移一层，每次+1，但不超过3
          newPriority = Math.min(currentPriority + 1, 3);
          break;
        }
        case 'moveDown': {
          // 下移一层，每次-1，但不低于0
          newPriority = Math.max(currentPriority - 1, 0);
          break;
        }
        default:
          return null;
      }

      // 检查新优先级是否与当前优先级相同，如果相同则不需要更新
      if (newPriority === currentPriority) {
        if (operation === 'moveToTop') {
          this.$Message.info({
            title: langMap.tip[this.lang],
            desc: langMap.layerAlreadyTop[this.lang]
          });
        } else if (operation === 'moveToBottom') {
          this.$Message.info({
            title: langMap.tip[this.lang],
            desc: langMap.layerAlreadyBottom[this.lang]
          });
        } else {
          this.$Message.info({
            title: langMap.tip[this.lang],
            desc: langMap.layerPriorityUnchanged[this.lang]
          });
        }
        return null;
      }

      // 检查新优先级是否会导致与同优先级图层交叉
      const conflictCheck = this.checkPriorityConflict(newPriority, currentFence.tempId, stationLayers);
      if (conflictCheck.hasConflict) {
        this.$Message.error(langMap.sameLayerConflict[this.lang]);
        return null;
      }

      // 返回单个图层的更新数据
      return {
        id,
        layerPriority: newPriority
      };
    },

    // 修改图层操作确认方法
    showLayerOperationConfirm(operation, message, sortData, currentFence) {
      // 弹出确认框并在确认后提交图层优先级变更
      this.$Modal.confirm({
        title: langMap.confirmOperation[this.lang],
        content: message,
        onOk: () => {
          this.pendingOperation = operation;
          this.pendingSortData = {
            stationId: currentFence.stationId,
            electFenceId: sortData.id,
            layerPriority: sortData.layerPriority
          };
          this.confirmSortChange();
        }
      });
    },

    // 确认排序更改 - 简化版本，只更新单个图层
    confirmSortChange() {
      // 调用接口更新图层优先级，成功后刷新列表并关闭面板
      if (!this.pendingSortData) return;

      // 调用API更新数据
      api.postData('task-server/fence/updateLayerPriority', this.pendingSortData)
        .then(res => {
          if (res.state == 0) {
            this.$Notice.error({
              title: langMap.tip[this.lang],
              desc: res.msg
            });
          } else {
            this.$Notice.success({
              title: langMap.tip[this.lang],
              desc: langMap.layerSortUpdateSuccess[this.lang]
            });
            // 关闭排序面板并清理数据
            this.isShowLayerSortPanel = false;
            this.pendingOperation = null;
            this.pendingSortData = null;

            // 直接触发父组件的刷新方法
            this.$emit('refreshData');
          }
        })
        .catch(err => {
          this.$Notice.error({
            title: langMap.tip[this.lang],
            desc: langMap.layerSortUpdateFailed[this.lang] + ': ' + (err.message || langMap.unknown[this.lang])
          });
        });
    },

    // 添加一个新方法，用于聚焦到指定ID的电子围栏区域
    focusOnFence(fenceId) {
      // 将地图视野聚焦到指定围栏图层的范围
      if (!fenceId) return;

      // 查找对应的图层
      let targetLayer = this.findLayerById(fenceId);

      if (targetLayer) {
        // 获取围栏的边界并将地图视图设置到该区域
        const bounds = targetLayer.getBounds();
        this.map.fitBounds(bounds, {
          padding: [50, 50], // 添加一些内边距使围栏完全可见
          maxZoom: 29,       // 限制最大缩放级别
          animate: true      // 使用动画过渡
        });
      }
    },
    // 查找并显示所有交叉区域
    findAndDisplayIntersections() {
      // 基于当前可见围栏/采集预览/合并预览计算并绘制交叉区域
      const visibleAreaLayers = this.map
        ? this.areaLayers.filter(layer => layer && this.map.hasLayer(layer))
        : this.areaLayers;

      const sourceLayers = [];
      sourceLayers.push(...visibleAreaLayers);
      if (this.collectPreviewLayer && this.map && this.map.hasLayer(this.collectPreviewLayer)) {
        sourceLayers.push(this.collectPreviewLayer);
      }
      if (this.mergePreviewLayer && this.map && this.map.hasLayer(this.mergePreviewLayer)) {
        sourceLayers.push(this.mergePreviewLayer);
      }

      this.intersectionLayers = mapIntersectionUtil.findAndDisplayIntersections(
        this.map,
        sourceLayers,
        this.intersectionLayers
      );
    },
    // 添加一个辅助方法，用于检查点击是否发生在指定元素上
    isClickOnElement(event, selector) {
      // 判断坐标点是否落在某个 selector 区域内（用于点击外部关闭面板）
      const elements = document.querySelectorAll(selector);
      for (let i = 0; i < elements.length; i++) {
        const rect = elements[i].getBoundingClientRect();
        if (
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom
        ) {
          return true;
        }
      }
      return false;
    },
    // 上移一层 - 简化版本
    moveLayerUp() {
      // 将当前围栏优先级上调一档（同基站内，最高 3）
      // 使用 checkFenceOperationAvailable 检查是否可以上移
      // 传递 true 和 true 参数来检查是否已经在最顶层
      const checkResult = this.checkFenceOperationAvailable(true, true);
      if (!checkResult.available) return;

      const { currentFence, stationLayers } = checkResult;
      const sortData = this.calculateSortData('moveUp', currentFence, stationLayers);

      if (sortData) {
        this.showLayerOperationConfirm('moveUp', langMap.confirmMoveUpLayer[this.lang], sortData, currentFence);
      }
    },
    // 下移一层 - 简化版本
    moveLayerDown() {
      // 将当前围栏优先级下调一档（同基站内，最低 0）
      // 使用 checkFenceOperationAvailable 检查是否可以下移
      // 传递 true 和 false 参数来检查是否已经在最底层
      const checkResult = this.checkFenceOperationAvailable(true, false);
      if (!checkResult.available) return;

      const { currentFence, stationLayers } = checkResult;
      const sortData = this.calculateSortData('moveDown', currentFence, stationLayers);

      if (sortData) {
        this.showLayerOperationConfirm('moveDown', langMap.confirmMoveDownLayer[this.lang], sortData, currentFence);
      }
    },
    // 设置图层优先级 - 修改为限制最高3
    setLayerPriority() {
      // 按输入值设置当前围栏优先级（0-3），并在确认后提交接口
      const currentFence = this.getCurrentFenceInfo();
      if (!currentFence) return;

      // 限制输入值在0到3之间
      let validPriority = Math.min(Math.max(this.newPriorityValue, 0), 3);

      // 如果输入值超出范围，更新显示
      if (validPriority !== this.newPriorityValue) {
        this.newPriorityValue = validPriority;
        this.$Notice.info({
          title: langMap.tip[this.lang],
          desc: langMap.priorityChanged[this.lang].replace('{priority}', validPriority)
        });
      }

      // 检查是否与当前值相同
      if (currentFence.layerPriority === validPriority) {
        this.$Notice.info({
          title: langMap.tip[this.lang],
          desc: langMap.priorityUnchanged[this.lang]
        });
        return;
      }

      // 获取同一基站下的所有围栏
      const stationLayers = this.getLayersInSameStation(currentFence.stationId);

      // 检查新优先级是否会导致交叉
      const conflictCheck = this.checkPriorityConflict(validPriority, currentFence.tempId, stationLayers);
      if (conflictCheck.hasConflict) {
        this.$Notice.error({
          title: langMap.tip[this.lang],
          desc: conflictCheck.message
        });
        return;
      }

      // 准备更新数据
      const sortData = {
        id: currentFence.id || currentFence.tempId,
        layerPriority: validPriority
      };

      // 显示确认对话框
      this.showLayerOperationConfirm(
        'setPriority',
        langMap.confirmSetLayerPriority[this.lang].replace('{priority}', validPriority),
        sortData,
        currentFence
      );
    },
    // 选择目标图层（用于拖放实现）
    selectTargetLayer(layerId) {
      // 选择目标围栏作为参考，便于快速设置优先级
      if (layerId === this.currentFenceId) return;

      this.targetLayerId = layerId;
      const targetLayer = this.findFenceById(layerId);
      const currentLayer = this.getCurrentFenceInfo();

      if (targetLayer && currentLayer) {
        // 设置新的优先级值为目标图层的优先级
        this.newPriorityValue = targetLayer.layerPriority || 0;

        // 提示用户
        this.$Notice.info({
          title: langMap.tip[this.lang],
          desc: langMap.selectedTargetLayer[this.lang].replace('{name}', targetLayer.name)
        });
      }
    },
    // 打开图层排序面板时加载当前基站的所有图层
    openLayerSortPanel(id) {
      // 打开图层排序面板并加载同基站围栏列表
      this.currentFenceId = id;
      const currentFence = this.getCurrentFenceInfo();

      if (currentFence && currentFence.stationId) {
        // 获取同一基站下的所有围栏
        const stationLayers = this.getLayersInSameStation(currentFence.stationId);

        // 按优先级排序
        this.stationLayersList = [...stationLayers].sort((a, b) => {
          const aPriority = a.layerPriority || 0;
          const bPriority = b.layerPriority || 0;
          return bPriority - aPriority; // 从高到低排序
        });

        // 设置当前图层的优先级为默认值
        this.newPriorityValue = currentFence.layerPriority || 0;
      }

      this.isShowLayerSortPanel = true;
    },
    // 置顶操作
    moveLayerToTop() {
      // 将当前围栏优先级置为最高（3）
      const checkResult = this.checkFenceOperationAvailable(true, true);
      if (!checkResult.available) return;

      const { currentFence, stationLayers } = checkResult;
      const sortData = this.calculateSortData('moveToTop', currentFence, stationLayers);

      if (sortData) {
        this.showLayerOperationConfirm('moveToTop', langMap.confirmMoveToTopLayer[this.lang], sortData, currentFence);
      }
    },
    // 置底操作
    moveLayerToBottom() {
      // 将当前围栏优先级置为最低（0）
      const checkResult = this.checkFenceOperationAvailable(true, false);
      if (!checkResult.available) return;

      const { currentFence, stationLayers } = checkResult;
      const sortData = this.calculateSortData('moveToBottom', currentFence, stationLayers);

      if (sortData) {
        this.showLayerOperationConfirm('moveToBottom', langMap.confirmMoveToBottomLayer[this.lang], sortData, currentFence);
      }
    },
    // 检查优先级冲突的方法优化
    checkPriorityConflict(newPriority, currentFenceId, stationLayers) {
      // 在优先级变更前检查同优先级围栏是否会产生交叉冲突
      // 获取当前图层
      const currentLayer = this.findLayerById(currentFenceId);
      if (!currentLayer) return { hasConflict: false };

      // 查找所有优先级相同或接近的图层
      const similarPriorityLayers = this.findSimilarPriorityLayers(newPriority, currentFenceId, stationLayers);

      // 如果没有优先级相同的图层，则没有冲突
      if (similarPriorityLayers.length === 0) {
        return { hasConflict: false };
      }
      //检查是否与优先级相同的图层有交叉
      for (const layer of similarPriorityLayers) {
        const otherLayer = this.findLayerById(layer.tempId);
        if (!otherLayer) continue;

        // 如果两个图层有交叉，则不允许调整
        if (this.checkLayersIntersection(currentLayer, otherLayer)) {
          return {
            hasConflict: true,
            message: langMap.sameLayerConflict[this.lang]
          };
        }
      }

      // 没有交叉冲突，允许调整
      return { hasConflict: false };
    },
    // 查找相似优先级的图层
    findSimilarPriorityLayers(priority, currentFenceId, stationLayers) {
      // 筛选同基站下与指定优先级相同/接近的围栏（排除自身）
      return stationLayers.filter(layer => {
        // 排除当前图层自身
        if (layer.tempId === currentFenceId) return false;

        const layerPriority = layer.layerPriority || 0;
        // 优先级相同或非常接近（浮点数精度问题）
        return Math.abs(layerPriority - priority) < 0.01;
      });
    },
    // 检查编辑中的图层是否与同优先级图层产生交叉 - 优化版本

    checkEditingConflict(editingLayer) {
      // 编辑过程中检查是否与同优先级围栏产生交叉
      if (!editingLayer || !editingLayer.options) return false;

      const currentFence = this.findFenceById(editingLayer.options.id);
      if (!currentFence || !currentFence.stationId) return false;

      // 获取当前图层的优先级
      const currentPriority = currentFence.layerPriority || 0;

      // 获取同一基站下的所有围栏
      const stationLayers = this.getLayersInSameStation(currentFence.stationId);

      // 查找所有优先级相同的图层
      const samePriorityLayers = this.findSimilarPriorityLayers(currentPriority, editingLayer.options.id, stationLayers);

      // 如果没有优先级相同的图层，则没有冲突
      if (samePriorityLayers.length === 0) return false;

      // 检查是否与优先级相同的图层有交叉或边界线重叠
      return this.checkConflictWithLayers(editingLayer, samePriorityLayers);
    },
    // 检查图层是否与一组图层有冲突
    checkConflictWithLayers(targetLayer, layersList) {
      // 检测目标图层与给定围栏列表是否存在交叉冲突
      for (const layer of layersList) {
        const otherLayer = this.findLayerById(layer.tempId);
        if (!otherLayer) continue;

        // 使用增强的交叉检测
        if (this.checkLayersIntersection(targetLayer, otherLayer)) {
          return true;
        }
      }

      return false;
    },
    // 添加一个统一的冲突处理方法，减少代码重复
    handleConflict(layer, layerId) {
      // 冲突发生时回滚围栏形状并提示
      // 尝试从多个来源获取原始形状
      const originalShape = this.originalLatLngs || this.originalLayerShapes[layerId];

      if (originalShape) {
        // 使用安全的恢复方法
        this.safelyRestoreShape(layer, originalShape);
        this.$Notice.error({
          title: langMap.tip[this.lang],
          desc: langMap.sameLayerConflict[this.lang]
        });
      }
    },
    // 安全地恢复图层形状的方法
    safelyRestoreShape(layer, originalShape,pointNoHas) {
      // 安全恢复图层坐标（带参数校验与编辑态恢复）
      try {
        // 检查是否为有效的多边形图层
        if (!layer || !layer.setLatLngs) {
          this.$Notice.error({
            title: langMap.tip[this.lang],
            desc: langMap.invalidLayer[this.lang]
          });
          return false;
        }

        // 检查原始形状是否有效
        if (!originalShape || !Array.isArray(originalShape) || originalShape.length === 0) {
          this.$Notice.error({
            title: langMap.tip[this.lang],
            desc: langMap.invalidOriginalShape[this.lang]
          });
          return false;
        }

        // 确保原始形状中的每个点都是有效的经纬度对象
        const validPoints = originalShape.filter(point =>
          point && typeof point.lat === 'number' && typeof point.lng === 'number'
        );

        if (validPoints.length < 3) {
          this.$Notice.error({
            title: langMap.tip[this.lang],
            desc: langMap.notEnoughValidPoints[this.lang]
          });
          return false;
        }

        // 安全地设置图层形状
        layer.setLatLngs([validPoints]);

        // 确保重新渲染
        layer.redraw();

        // 重新启用编辑模式以显示编辑点
        if (layer.pm && !pointNoHas) {
          // 如果当前处于编辑模式，先禁用再启用
          if (layer.pm.enabled()) {
            layer.pm.disable();
            setTimeout(() => {
              layer.pm.enable({
                allowSelfIntersection: false,
              });
            }, 10);
          }
        }

        return true;
      } catch (error) {
        this.$Notice.error({
          title: langMap.tip[this.lang],
          desc: langMap.restoreShapeError[this.lang] + ': ' + (error.message || langMap.unknown[this.lang])
        });
        return false;
      }
    },

    toggleFenceToolsMenu() {
      // 展开/收起围栏工具下拉菜单
      this.fenceToolsMenuOpen = !this.fenceToolsMenuOpen;
      this.map && this.map.closePopup && this.map.closePopup();
    },
    // 工具菜单“可点但提示原因”：统一返回不可用原因（空字符串表示可用）
    getFenceToolDisabledReason(type) {
      // 判断工具项是否可用，并返回不可用原因文案
      if (type === 'cut') {
        if (this.mergePreviewActive) return langMap.pleaseCancelMergePreviewFirst[this.lang];
        if (this.isMergeModeActive) return langMap.mergeModeActiveCannotCut[this.lang];
        if (!this.currentFenceId && !this.isCutModeActive) return langMap.pleaseSelectFenceToCut[this.lang];
        return '';
      }
      if (type === 'merge') {
        if (this.isCutModeActive) return langMap.cutModeActiveCannotMerge[this.lang];
        if (this.mergePreviewActive) return langMap.pleaseCancelMergePreviewFirst[this.lang];
        return '';
      }
      return '';
    },
    pickFenceTool(type) {
      // 围栏工具点击入口（不可用则提示，可用则切换对应模式）
      const reason = this.getFenceToolDisabledReason(type);
      if (reason) {
        // 仅提示原因时保持下拉不消失
        this.$Message.warning(reason);
        return;
      }
      this.fenceToolsMenuOpen = false;
      if (type === 'cut') {
        this.toggleCutMode();
        return;
      }
      if (type === 'merge') {
        this.toggleMergeMode();
      }
    },

    // 围栏切割功能
    toggleCutMode() {
      // 切换围栏切割模式（进入时展示可选顶点，退出时清理状态）
      if (!this.isEdit) {
        this.$Message.warning(langMap.pleaseEnterEditMode[this.lang]);
        return;
      }

      if (this.isCutModeActive) {
        this.cancelCutMode();
      } else {
        this.activateCutMode();
      }
    },

    // 激活切割模式
    activateCutMode() {
      // 进入切割模式并初始化切割点选择与提示
      // 确保选中了一个围栏
      if (!this.currentFenceId) {
        this.$Message.warning(langMap.pleaseSelectFenceToCut[this.lang]);
        return;
      }

      this.currentCutFenceId = this.currentFenceId;
      const fence = this.findLayerById(this.currentCutFenceId);

      if (!fence || !fence._latlngs || !fence._latlngs[0]) {
        this.$Message.error(langMap.invalidFenceData[this.lang]);
        return;
      }

      // 先禁用常规编辑模式
      this.map.pm.disableGlobalEditMode();

      // 禁用全局拖拽模式，防止与切割模式冲突
      this.map.pm.disableGlobalDragMode();

      // 禁用当前围栏的拖拽功能
      if (fence.pm && fence.pm.disableLayerDrag) {
        fence.pm.disableLayerDrag();
      }

      // 显示所有顶点为可选择状态
      this.showVerticesForCutting(fence);

      // 重置切割点
      this.cutPoints = [];

      // 激活切割模式
      this.isCutModeActive = true;

      // 添加提示
      this.$Message.info(langMap.selectTwoCutPoints[this.lang]);
      this.map.closePopup();
    },

    // 显示围栏顶点供选择
    showVerticesForCutting(fence) {
      // 渲染围栏所有顶点 marker，允许用户选择切割点
      // 清除之前的标记
      this.clearOriginalVertexMarkers();

      // 获取围栏的所有顶点
      const vertices = fence._latlngs[0];

      // 为每个顶点创建一个特殊的标记
      vertices.forEach((vertex, index) => {
        const marker = L.circleMarker([vertex.lat, vertex.lng], {
          radius: 8,
          color: '#FF00FF', /* 品红色边框 */
          fillColor: '#FF00FF', /* 品红色填充 */
          fillOpacity: 0.8,
          weight: 3,
          vertexIndex: index
        }).addTo(this.map);

        // 添加点击事件和悬停效果
        marker.on('click', (e) => { this.onVertexClick(e, vertex, index); });
        marker.on('mouseover', () => {
          marker.setStyle({ radius: 10, fillOpacity: 1.0, weight: 4 });
        });
        marker.on('mouseout', () => {
          marker.setStyle({ radius: 8, fillOpacity: 0.8, weight: 3 });
        });

        this.originalVertexMarkers.push(marker);
      });
    },

    // 顶点点击事件处理
    onVertexClick(e, vertex, index) {
      // 记录用户选择的切割点，并在选满两点后绘制切割线
      // 防止事件冒泡
      L.DomEvent.stopPropagation(e);

      // 如果已选择了两个点，则不再添加
      if (this.cutPoints.length >= 2) {
        this.$Message.warning(langMap.alreadySelectedTwoPoints[this.lang]);
        return;
      }

      // 添加到切割点数组
      this.cutPoints.push({
        latlng: vertex,
        index: index
      });

      // 高亮显示选中的点
      e.target.setStyle({
        color: '#32CD32',
        fillColor: '#32CD32',
        radius: 8,
        fillOpacity: 0.9
      });

      // 如果已选择两个点，显示连线
      if (this.cutPoints.length === 2) {
        this.showCutLine();
      }
    },

    // 显示切割线
    showCutLine() {
      // 绘制两切割点之间的辅助线，并在中点弹出“执行切割”按钮
      // 如果已有切割线，先移除
      if (this.cutLineLayer) {
        this.map.removeLayer(this.cutLineLayer);
      }

      // 创建更醒目的切割线
      this.cutLineLayer = L.polyline([
        [this.cutPoints[0].latlng.lat, this.cutPoints[0].latlng.lng],
        [this.cutPoints[1].latlng.lat, this.cutPoints[1].latlng.lng]
      ], {
        color: '#FF00FF', /* 品红色切割线 */
        weight: 5,
        dashArray: '10, 10',
        opacity: 1.0,
        lineCap: 'round'
      }).addTo(this.map);

      // 添加切割按钮
      this.$nextTick(() => {
        // 计算切割线的中点位置
        const midLat = (this.cutPoints[0].latlng.lat + this.cutPoints[1].latlng.lat) / 2;
        const midLng = (this.cutPoints[0].latlng.lng + this.cutPoints[1].latlng.lng) / 2;

        // 创建一个弹出窗口容器和按钮
        const cutButtonContainer = document.createElement('div');
        cutButtonContainer.className = 'cut-button-container';

        const cutButton = document.createElement('button');
        cutButton.className = 'cut-execute-btn';
        cutButton.innerHTML = langMap.executeCut[this.lang];
        cutButton.onclick = () => this.executeCut();

        cutButtonContainer.appendChild(cutButton);

        // 创建一个弹出窗口
        const popup = L.popup({
          closeButton: false,
          offset: [0, -5],
          className: 'cut-popup'
        })
          .setLatLng([midLat, midLng])
          .setContent(cutButtonContainer)
          .openOn(this.map);
      });
    },

    // 执行切割操作
    executeCut() {
      // 按两切割点把围栏拆分成两个多边形，并提交切割保存
      if (this.cutPoints.length !== 2) {
        this.$Notice.error({
          title: langMap.tip[this.lang],
          desc: langMap.cutPointsNotEnough[this.lang]
        });
        return;
      }

      try {
        const fence = this.findLayerById(this.currentCutFenceId);

        if (!fence || !fence._latlngs || !fence._latlngs[0]) {
          this.$Notice.error({
            title: langMap.tip[this.lang],
            desc: langMap.invalidFenceData[this.lang]
          });
          return;
        }

        const vertices = [...fence._latlngs[0]];

        // 确保索引按正确顺序排列
        let startIdx = this.cutPoints[0].index;
        let endIdx = this.cutPoints[1].index;

        // 如果两个索引相邻，无法切割
        if (Math.abs(startIdx - endIdx) === 1 ||
            Math.abs(startIdx - endIdx) === vertices.length - 1) {
          this.$Notice.error({
            title: langMap.tip[this.lang],
            desc: langMap.adjacentPointsCantCut[this.lang]
          });
          return;
        }

        // 创建两个新多边形
        const polygon1 = [];
        const polygon2 = [];

        // 确保索引的顺序是合理的 (可能需要调整)
        if (startIdx > endIdx) {
          [startIdx, endIdx] = [endIdx, startIdx];
        }

        // 第一个多边形：包含从0到startIdx的点，以及从endIdx到最后的点
        // 形成的路径是: 0->1->...->startIdx->endIdx->...->最后一个点
        for (let i = 0; i <= startIdx; i++) {
          polygon1.push(vertices[i]);
        }
        for (let i = endIdx; i < vertices.length; i++) {
          polygon1.push(vertices[i]);
        }

        // 第二个多边形：包含从startIdx到endIdx的点
        // 形成的路径是: startIdx->startIdx+1->...->endIdx
        for (let i = startIdx; i <= endIdx; i++) {
          polygon2.push(vertices[i]);
        }

        // 检查多边形是否有效 (至少3个点)
        if (polygon1.length < 3 || polygon2.length < 3) {
          this.$Notice.error({
            title: langMap.tip[this.lang],
            desc: langMap.invalidPolygonAfterCut[this.lang]
          });
          return;
        }

        // 将多边形点转换为API所需的格式
        const areaOne = this.convertPolygonToApiFormat(polygon1);
        const areaTwo = this.convertPolygonToApiFormat(polygon2);

        // 提交到后端
        this.submitCutFenceData(areaOne, areaTwo);

        // 清理切割模式
        this.cancelCutMode();

      } catch (error) {
        this.$Notice.error({
          title: langMap.tip[this.lang],
          desc: langMap.cutFailed[this.lang] + ': ' + (error.message || langMap.unknown[this.lang])
        });
      }
    },

    // 转换多边形数据为API格式
    convertPolygonToApiFormat(polygon) {
      // 将 Leaflet 点数组转换为后端所需的 lon/lat/type 结构
      return polygon.map(point => ({
        lon: point.lng.toString(),
        lat: point.lat.toString(),
        type: 0 // 默认使用WGS坐标系
      }));
    },

    // 提交切割数据到后端
    submitCutFenceData(areaOne, areaTwo) {
      // 调用切割接口保存切割结果，并在成功后刷新围栏数据
      // 获取当前围栏信息
      const currentFence = this.findFenceById(this.currentCutFenceId);
      if (!currentFence) return;

      const submitData = {
        electFenceId: this.currentCutFenceId,
        areaOne: JSON.stringify(areaOne),
        areaTwo: JSON.stringify(areaTwo),
        type:0
      };

      // 调用API保存切割后的围栏
      api.postData('task-server/fence/cutLayer', submitData)
        .then(res => {
          if (res.state != 0) {
            this.$Notice.success({
              title: langMap.tip[this.lang],
              desc: langMap.cutSuccess[this.lang]
            });
            // 触发父组件刷新围栏列表
            this.$emit('refreshData');
          } else {
            this.$Notice.error({
              title: langMap.tip[this.lang],
              desc: langMap.cutFailed[this.lang] + ': ' + (res.msg || langMap.unknown[this.lang])
            });
          }
        })
        .catch(err => {
          this.$Notice.error({
            title: langMap.tip[this.lang],
            desc: langMap.cutFailed[this.lang] + ': ' + (err.message || langMap.unknown[this.lang])
          });
        });
    },

    // 取消切割模式
    cancelCutMode() {
      // 退出切割模式并清理切割相关图层/状态，恢复编辑提示
      // 清除切割点
      this.cutPoints = [];
      this.currentCutFenceId = null;

      // 清除标记和切割线
      this.clearOriginalVertexMarkers();

      if (this.cutLineLayer) {
        this.map.removeLayer(this.cutLineLayer);
        this.cutLineLayer = null;
      }

      // 关闭所有弹出窗口
      this.map.closePopup();

      // 禁用切割模式
      this.isCutModeActive = false;

      // 如果是编辑模式中，恢复到编辑提示状态
      if (this.isEdit) {
        // 如果当前正在编辑某个围栏，保持该围栏的编辑状态
        if (this.currentFenceId) {
          const targetLayer = this.findLayerById(this.currentFenceId);
          if (targetLayer) {
            // 重新激活当前围栏的编辑功能
            targetLayer.pm.enable({
              allowSelfIntersection: false,
            });

            // 重新启用拖拽功能
            if (targetLayer.pm && targetLayer.pm.enableLayerDrag) {
              targetLayer.pm.enableLayerDrag();
            }
          } else {
            // 如果没有当前编辑的围栏，显示提示
            this.showEditModeMessage();
          }
        } else {
          // 如果没有选中的围栏，显示提示
          this.showEditModeMessage();
        }
      }
    },

    toggleMergeMode() {
      // 切换合并模式（进入/退出）
      if (this.isMergeModeActive) {
        this.cancelMergeMode();
        return;
      }
      this.activateMergeMode();
    },
    activateMergeMode() {
      // 进入合并模式并初始化多选状态与交互限制
      if (!this.isEdit) {
        this.$Message.warning(langMap.pleaseEnterEditModeForMerge[this.lang]);
        return;
      }
      if (this.isCutModeActive) {
        this.$Message.warning(langMap.cutModeActiveCannotMerge[this.lang]);
        return;
      }
      if (this.mergePreviewActive) {
        this.cancelMergePreview(false);
      }
      this.disableAllFencesEdit();
      this.currentFenceId = null;
      this.isMergeModeActive = true;
      this.mergeSelectedFenceIds = [];
      this.mergeSelectionStyleBackup = {};
      this.$Message.info(langMap.selectFencesToMerge[this.lang]);
      this.map && this.map.closePopup && this.map.closePopup();
    },
    cancelMergeMode() {
      // 退出合并模式并恢复围栏样式/清空选中
      if (!this.isMergeModeActive) return;
      this.restoreMergeSelectionStyles();
      this.isMergeModeActive = false;
      this.mergeSelectedFenceIds = [];
      this.mergeSelectionStyleBackup = {};
      this.currentFenceId = null;
      this.$emit('activeFenceChanged', null);
      if (this.isEdit) {
        this.disableAllFencesEdit();
      }
    },
    restoreMergeSelectionStyles() {
      // 恢复合并选择前缓存的围栏样式
      const backupMap = this.mergeSelectionStyleBackup || {};
      const ids = Object.keys(backupMap);
      ids.forEach(id => {
        const layer = this.findLayerByIdLoose(id);
        const backup = backupMap[id];
        if (layer && backup && layer.setStyle) {
          layer.setStyle(backup);
          layer.redraw && layer.redraw();
        }
        if (layer && layer._path && this.isEdit) {
          layer._path.style.opacity = '0.5';
        }
      });
    },
    toggleMergeFenceSelection(fenceId) {
      // 合并模式下切换围栏选中状态，并按规则校验与高亮
      if (!fenceId) return;
      const collectId = this.collectPreview && this.collectPreview.id ? this.collectPreview.id : '__collect_preview__';
      if (fenceId === collectId) return;
      const layer = this.findLayerByIdLoose(fenceId);
      if (!layer) return;

      const current = Array.isArray(this.mergeSelectedFenceIds) ? this.mergeSelectedFenceIds : [];
      const idx = current.indexOf(fenceId);
      if (idx !== -1) {
        current.splice(idx, 1);
        const backup = this.mergeSelectionStyleBackup ? this.mergeSelectionStyleBackup[fenceId] : null;
        if (backup && layer.setStyle) {
          layer.setStyle(backup);
          layer.redraw && layer.redraw();
        }
        if (this.mergeSelectionStyleBackup && this.mergeSelectionStyleBackup[fenceId]) {
          delete this.mergeSelectionStyleBackup[fenceId];
        }
        this.mergeSelectedFenceIds = [...current];
        return;
      }

      const validation = this.validateMergeCandidate(fenceId, current);
      if (!validation.ok) {
        // 合并校验提示改为 Message（居中显示），与切割模式提示一致
        this.$Message.warning(validation.reason);
        return;
      }

      if (!this.mergeSelectionStyleBackup[fenceId]) {
        this.mergeSelectionStyleBackup[fenceId] = {
          color: layer.options && layer.options.color,
          weight: layer.options && layer.options.weight,
          opacity: layer.options && layer.options.opacity,
          fillColor: layer.options && layer.options.fillColor,
          fillOpacity: layer.options && layer.options.fillOpacity
        };
      }
      if (layer.setStyle) {
        layer.setStyle({
          color: '#7C3AED',
          weight: 5,
          opacity: 1,
          fillOpacity: Math.max(0.05, (layer.options && layer.options.fillOpacity) || 0.2)
        });
      }
      this.mergeSelectedFenceIds = [...current, fenceId];
    },
    validateMergeCandidate(candidateId, selectedIds) {
      // 校验候选围栏是否满足合并条件（同站/同类型/同优先级/连通）
      const baseId = Array.isArray(selectedIds) && selectedIds.length > 0 ? selectedIds[0] : null;
      if (!baseId) return { ok: true, reason: '' };

      const baseFence = this.findFenceById(baseId);
      const candidateFence = this.findFenceById(candidateId);
      if (!baseFence || !candidateFence) {
        return { ok: false, reason: langMap.mergeFailedInvalidFences[this.lang] };
      }

      const baseStationId = baseFence.stationId != null ? baseFence.stationId : null;
      const candStationId = candidateFence.stationId != null ? candidateFence.stationId : null;
      if (baseStationId != null && candStationId != null && baseStationId !== candStationId) {
        return { ok: false, reason: langMap.mergeRequireSameStation[this.lang] };
      }

      if (baseFence.type != null && candidateFence.type != null && baseFence.type !== candidateFence.type) {
        return { ok: false, reason: langMap.mergeRequireSameType[this.lang] };
      }

      const basePriority = baseFence.layerPriority != null ? baseFence.layerPriority : null;
      const candPriority = candidateFence.layerPriority != null ? candidateFence.layerPriority : null;
      if (basePriority != null && candPriority != null && Math.abs(basePriority - candPriority) > 0.001) {
        return { ok: false, reason: langMap.mergeRequireSamePriority[this.lang] };
      }

      const nextIds = [...selectedIds, candidateId];
      const union = this.tryUnionFenceIds(nextIds);
      if (!union.ok) {
        return { ok: false, reason: union.reason };
      }
      if (union.solutionCount > 1) {
        return { ok: false, reason: langMap.mergeRequireConnected[this.lang] };
      }
      return { ok: true, reason: '' };
    },
    tryUnionFenceIds(ids) {
      // 用 Clipper 对多围栏做 union，返回合并结果及连通性
      const list = Array.isArray(ids) ? ids : [];
      const layers = list.map(id => this.findLayerByIdLoose(id)).filter(Boolean);
      const polys = layers
        .map(l => (l && l._latlngs && l._latlngs[0]) ? l._latlngs[0] : null)
        .filter(arr => Array.isArray(arr) && arr.length >= 3);
      if (polys.length < 2) return { ok: false, reason: langMap.mergeFailedInvalidFences[this.lang], solutionCount: 0, merged: null };

      // 收集所有原始顶点，用于合并后坐标吸附（保留原始精度）
      const allOriginalPts = [];
      polys.forEach(poly => {
        poly.forEach(p => {
          if (p && p.lat != null && p.lng != null) {
            allOriginalPts.push({ lat: p.lat, lng: p.lng });
          }
        });
      });

      const scale = 1e10;
      const subj = new ClipperLib.Paths();
      polys.forEach(poly => {
        const cleaned = poly.filter(p => p && p.lat != null && p.lng != null);
        const path = cleaned.map(p => ({ X: Math.round(p.lng * scale), Y: Math.round(p.lat * scale) }));
        subj.push(path);
      });

      const solution = new ClipperLib.Paths();
      try {
        const c = new ClipperLib.Clipper();
        c.AddPaths(subj, ClipperLib.PolyType.ptSubject, true);
        const succeeded = c.Execute(
          ClipperLib.ClipType.ctUnion,
          solution,
          ClipperLib.PolyFillType.pftNonZero,
          ClipperLib.PolyFillType.pftNonZero
        );
        if (!succeeded || !solution || solution.length === 0) {
          return { ok: false, reason: langMap.mergeFailed[this.lang], solutionCount: 0, merged: null };
        }
      } catch (e) {
        return { ok: false, reason: langMap.mergeFailed[this.lang], solutionCount: 0, merged: null };
      }

      let best = solution[0];
      let bestArea = Math.abs(ClipperLib.Clipper.Area(best));
      for (let i = 1; i < solution.length; i++) {
        const area = Math.abs(ClipperLib.Clipper.Area(solution[i]));
        if (area > bestArea) {
          best = solution[i];
          bestArea = area;
        }
      }

      // 吸附容差：1e-8 度约 1mm，足以覆盖整数化误差，同时不会误吸附相邻点
      const SNAP_TOL = 1e-8;
      const mergedLatLngs = best.map(pt => {
        const lat = pt.Y / scale;
        const lng = pt.X / scale;
        // 尝试吸附到原始顶点，保留原坐标精度
        for (let i = 0; i < allOriginalPts.length; i++) {
          const orig = allOriginalPts[i];
          if (Math.abs(orig.lat - lat) < SNAP_TOL && Math.abs(orig.lng - lng) < SNAP_TOL) {
            return { lat: orig.lat, lng: orig.lng };
          }
        }
        // 真正的新交叉点，使用 Clipper 计算值
        return { lat, lng };
      }).filter(p => !Number.isNaN(p.lat) && !Number.isNaN(p.lng));

      return { ok: true, reason: '', solutionCount: solution.length, merged: mergedLatLngs };
    },
    applyMergeSelection(ids) {
      // 根据 ids 应用合并选中样式（用于恢复已选状态）
      const list = Array.isArray(ids) ? ids : [];
      this.mergeSelectedFenceIds = [];
      this.mergeSelectionStyleBackup = {};

      list.forEach(id => {
        const layer = this.findLayerByIdLoose(id);
        if (!layer) return;
        const key = id != null ? String(id) : '';
        this.mergeSelectionStyleBackup[key] = {
          color: layer.options && layer.options.color,
          weight: layer.options && layer.options.weight,
          opacity: layer.options && layer.options.opacity,
          fillColor: layer.options && layer.options.fillColor,
          fillOpacity: layer.options && layer.options.fillOpacity
        };
        if (layer.setStyle) {
          layer.setStyle({
            color: '#7C3AED',
            weight: 5,
            opacity: 1,
            fillOpacity: Math.max(0.05, (layer.options && layer.options.fillOpacity) || 0.2)
          });
          layer.redraw && layer.redraw();
        }
        this.mergeSelectedFenceIds.push(id);
      });
    },
    confirmMergeMode() {
      // 对当前选择的围栏执行合并预览计算并展示预览层
      const ids = Array.isArray(this.mergeSelectedFenceIds) ? this.mergeSelectedFenceIds : [];
      if (ids.length < 2) {
        this.$Message.warning(langMap.pleaseSelectAtLeastTwoFencesToMerge[this.lang]);
        return;
      }

      // 先显示加载动画，再将重计算延迟到下一宏任务，让浏览器有机会渲染 Spin
      this.$emit('areasRenderStart');
      this.$nextTick(() => {
        setTimeout(() => {
          const union = this.tryUnionFenceIds(ids);
          if (!union.ok) {
            this.$Message.error(union.reason);
            this.emitAreasRendered();
            return;
          }
          if (union.solutionCount > 1) {
            // 不连通/不相邻：提示改为 Message（居中显示）
            this.$Message.warning(langMap.mergeRequireConnected[this.lang]);
            this.emitAreasRendered();
            return;
          }
          if (!union.merged || union.merged.length < 3) {
            this.$Message.error(langMap.mergeFailed[this.lang]);
            this.emitAreasRendered();
            return;
          }

          this.cancelMergeMode();
          this.applyMergePreview(union.merged, ids);
          this.$emit('mergePreviewCreated', { latlngs: union.merged, sourceIds: ids });
          // applyMergePreview -> scheduleIntersectionsRecalc -> emitAreasRendered 自动结束 loading
        }, 0);
      });
    },
    applyMergePreview(latlngs, sourceIds) {
      // 生成合并预览图层（紫色），并隐藏原围栏图层
      this.cancelMergePreview(false);
      if (!this.map) return;

      this.mergePreviewActive = true;
      this.mergePreviewSourceIds = Array.isArray(sourceIds) ? [...sourceIds] : [];
      this.mergePreviewLatLngs = Array.isArray(latlngs) ? [...latlngs] : [];
      this.mergeNewFenceName = '';

      this.mergePreviewHiddenStyleBackup = {};
      this.mergePreviewSourceIds.forEach(id => {
        const layer = this.findLayerByIdLoose(id);
        if (!layer) return;
        this.mergePreviewHiddenStyleBackup[id] = {
          color: layer.options && layer.options.color,
          weight: layer.options && layer.options.weight,
          opacity: layer.options && layer.options.opacity,
          fillColor: layer.options && layer.options.fillColor,
          fillOpacity: layer.options && layer.options.fillOpacity,
          interactive: layer.options && layer.options.interactive,
          wasOnMap: this.map && this.map.hasLayer ? this.map.hasLayer(layer) : false
        };
        if (layer.options) layer.options.interactive = false;
        if (this.map && this.map.removeLayer && this.map.hasLayer && this.map.hasLayer(layer)) {
          try {
            this.map.removeLayer(layer);
          } catch (e) {
          }
        } else if (layer.setStyle) {
          layer.setStyle({ opacity: 0, fillOpacity: 0 });
        }
      });

      this.mergePreviewLayer = L.polygon(latlngs, {
        color: '#7C3AED',
        weight: 3,
        opacity: 1,
        fillColor: '#7C3AED',
        fillOpacity: 0.12,
        interactive: false
      }).addTo(this.map);
      this.scheduleIntersectionsRecalc();
    },
    cancelMergePreview(reopenMergeMode = true, keepSelection = true) {
      // 取消合并预览并恢复原围栏显示/交互，可选恢复合并模式及选中
      // 有预览层时显示加载动画（scheduleIntersectionsRecalc -> emitAreasRendered 结束）
      if (this.mergePreviewActive) {
        this.$emit('areasRenderStart');
      }
      if (this.mergePreviewLayer && this.map && this.map.removeLayer) {
        try {
          this.map.removeLayer(this.mergePreviewLayer);
        } catch (e) {
        }
      }
      this.mergePreviewLayer = null;

      const ids = Array.isArray(this.mergePreviewSourceIds) ? this.mergePreviewSourceIds : [];
      ids.forEach(id => {
        const layer = this.findLayerByIdLoose(id);
        const backup = this.mergePreviewHiddenStyleBackup ? this.mergePreviewHiddenStyleBackup[id] : null;
        if (!layer) return;
        if (backup) {
          layer.options.interactive = backup.interactive;
          if (backup.wasOnMap && this.map && this.map.addLayer) {
            try {
              this.map.addLayer(layer);
            } catch (e) {
            }
          }
          if (layer.setStyle) {
            layer.setStyle({
              color: backup.color,
              weight: backup.weight,
              opacity: backup.opacity,
              fillColor: backup.fillColor,
              fillOpacity: backup.fillOpacity
            });
          }
          if (layer._path && this.isEdit) {
            layer._path.style.opacity = '0.5';
          }
        }
      });
      this.mergePreviewHiddenStyleBackup = {};
      this.mergePreviewSourceIds = [];
      this.mergePreviewActive = false;
      this.mergePreviewLatLngs = [];
      this.mergeNewFenceName = '';
      this.mergeSaving = false;
      this.scheduleIntersectionsRecalc();

      if (reopenMergeMode) {
        if (!this.isEdit) return;
        if (this.isCutModeActive) return;
        this.disableAllFencesEdit();
        this.currentFenceId = null;
        this.isMergeModeActive = true;
        if (keepSelection && ids.length) {
          this.applyMergeSelection(ids);
        } else {
          this.mergeSelectedFenceIds = [];
          this.mergeSelectionStyleBackup = {};
        }
      }
    },
    saveMergePreview() {
      // 提交合并保存接口，把预览围栏保存为新围栏
      if (!this.mergePreviewActive) return;
      if (this.mergeSaving) return;

      const name = (this.mergeNewFenceName || '').trim();
      if (!name) {
        this.$Message.warning(langMap.nameRequired[this.lang]);
        return;
      }

      const ids = Array.isArray(this.mergePreviewSourceIds) ? this.mergePreviewSourceIds : [];
      const electFenceIds = ids.map(v => Number(v)).filter(v => Number.isFinite(v));
      if (electFenceIds.length < 2) {
        this.$Message.error(langMap.mergeFailedInvalidFences[this.lang]);
        return;
      }

      const latlngs = (Array.isArray(this.mergePreviewLatLngs) && this.mergePreviewLatLngs.length)
        ? this.mergePreviewLatLngs
        : (this.mergePreviewLayer && this.mergePreviewLayer.getLatLngs
          ? ((this.mergePreviewLayer.getLatLngs()[0] || []).map(p => ({ lat: p.lat, lng: p.lng })))
          : []);

      if (!Array.isArray(latlngs) || latlngs.length < 3) {
        this.$Message.error(langMap.mergeFailed[this.lang]);
        return;
      }

      // 合并保存：沿用切割提交结构，坐标转换复用 convertPolygonToApiFormat
      const area = this.convertPolygonToApiFormat(latlngs);
      const submitData = {
        electFenceIds,
        name,
        area: JSON.stringify(area)
      };

      this.mergeSaving = true;
      // 合并保存接口：task-server/fence/mergeLayer
      api.postData('task-server/fence/mergeLayer', submitData)
        .then(res => {
          if (res.state != 0) {
            this.cancelMergePreview(false, false);
            this.cancelMergeMode();
            this.$Notice.success({
              title: langMap.tip[this.lang],
              desc: langMap.mergeSaveSuccess[this.lang]
            });
            this.$emit('refreshData');
          } else {
            this.$Notice.error({
              title: langMap.tip[this.lang],
              desc: langMap.mergeSaveFailed[this.lang] + ': ' + (res.msg || langMap.unknown[this.lang])
            });
          }
        })
        .catch(err => {
          this.$Notice.error({
            title: langMap.tip[this.lang],
            desc: langMap.mergeSaveFailed[this.lang] + ': ' + (err.message || langMap.unknown[this.lang])
          });
        })
        .finally(() => {
          this.mergeSaving = false;
        });
    },

    // 清除顶点标记
    clearOriginalVertexMarkers() {
      // 移除切割模式使用的顶点 marker 图层
      if (this.originalVertexMarkers.length > 0) {
        this.originalVertexMarkers.forEach(marker => {
          this.map.removeLayer(marker);
        });
        this.originalVertexMarkers = [];
      }
    },
    // 添加新方法来激活单个围栏的编辑功能
    activateFenceEdit(fenceId) {
      // 在编辑模式下仅激活指定围栏的编辑/拖拽能力
      // 获取要编辑的图层
      const targetLayer = this.findLayerById(fenceId);
      if (!targetLayer) {
        this.$Notice.error({
          title: langMap.tip[this.lang],
          desc: langMap.invalidFenceData[this.lang]
        });
        return;
      }

      // 关闭所有弹出窗口
      this.map.closePopup();

      // 禁用所有围栏的编辑功能
      this.disableAllFencesEdit();

      // 设置当前编辑的围栏ID
      this.currentFenceId = fenceId;
      this.applyCollectPreviewEditingState();
      // 只有在编辑不同围栏时才重置状态
      if (this.currentEditingFenceId !== fenceId) {
        // 重置未保存状态（开始编辑新围栏时）
        this.hasUnsavedChanges = false;
        this.pendingChanges = null;
        this.currentEditingFenceId = null;
        // 重置拖拽模式状态
        this.isDragModeActive = false;
      }

      // 通知父组件当前选中的围栏ID，用于高亮表格行
      this.$emit('activeFenceChanged', fenceId);

      // 显示当前图层，启用编辑和拖拽
      targetLayer._path.style.opacity = '1';

      // 启用编辑模式
      targetLayer.pm.enable({
        allowSelfIntersection: false,
      });
      // 启用当前图层的拖拽功能
      targetLayer.pm.enableLayerDrag();
      // 优化拖拽性能设置
      if (this.map.pm.globalOptions) {
        this.map.pm.globalOptions.continueDrawing = false;
        this.map.pm.globalOptions.snapDistance = 0; // 禁用吸附以提高性能
      }

      // 聚焦到这个图层
      this.focusOnFence(fenceId);

      // 保存原始形状和位置
      this.saveOriginalShape(targetLayer);
      // 为这个图层添加编辑事件监听
      this.clearLayerEvents(targetLayer);
      targetLayer.on("pm:editmodetoggled", e => this.handleEditModeToggled(e, targetLayer));
      targetLayer.on("pm:editstart", e => this.handleEditStart(e, targetLayer));
      targetLayer.on("pm:vertexadded", e => this.handleEditEvent(e, "vertexadded"));
      targetLayer.on("pm:vertexremoved", e => this.handleEditEvent(e, "vertexremoved"));
      targetLayer.on("pm:edit", e => this.handleEditEvent(e, "edit"));
      targetLayer.on("pm:markerdragend", e => this.handleDragEnd(e));

      // 添加拖拽事件监听 - 使用正确的事件名称
      targetLayer.on("pm:dragstart", e => this.handleDragStart(e));
      targetLayer.on("pm:drag", e => this.handleDrag(e));
      targetLayer.on("pm:dragend", e => this.handleDragEndFence(e));

      // 显示编辑成功激活的提示
      const fence = this.findFenceById(fenceId);
      /* if (fence) {
        this.$Message.info(`${langMap.editingFence[this.lang]}: ${fence.name}`);
      } */

      // 移除编辑提示，因为已经选择了围栏
      this.removeEditTipLayer();
    },

    selectCollectPreview(shouldFocus = true) {
      // 选中采集预览围栏（可选聚焦），并按编辑状态做切换拦截
      const collectId = this.collectPreview && this.collectPreview.id ? this.collectPreview.id : '__collect_preview__';
      if (!this.collectPreview || !this.collectPreview.active) return;
      if (!this.collectPreviewLayer) {
        this.renderCollectPreviewLayer();
      }
      if (this.isEdit) {
        if (this.hasUnsavedChanges && this.currentEditingFenceId && this.currentEditingFenceId !== collectId) {
          this.$Notice.warning({
            title: langMap.tip[this.lang],
            desc: langMap.pleaseFinishCurrentEdit[this.lang]
          });
          return;
        }
        if (this.collectPreviewHasUnsavedChanges && this.currentFenceId && this.currentFenceId !== collectId) {
          this.$Notice.warning({
            title: langMap.tip[this.lang],
            desc: langMap.pleaseFinishCurrentEdit[this.lang]
          });
          return;
        }
        this.map.closePopup();
        this.disableAllFencesEdit();
      }
      this.currentFenceId = collectId;
      this.$emit('activeFenceChanged', collectId);
      if (shouldFocus) this.focusOnCollectPreview();
      this.applyCollectPreviewEditingState();
    },
    // 添加一个新方法来禁用所有围栏的编辑功能
    disableAllFencesEdit() {
      // 禁用所有围栏编辑/拖拽并设置半透明（用于单围栏编辑/切割/合并等模式）
      // 遍历所有围栏图层，禁用编辑功能
      this.areaLayers.forEach(layer => {
        if (layer && layer.pm) {
          // 禁用编辑模式
          layer.pm.disable();

          // 禁用拖拽模式
          if (layer.pm.disableLayerDrag) {
            layer.pm.disableLayerDrag();
          }

          // 设置半透明样式
          if (layer._path) {
            layer._path.style.opacity = '0.5';
          }
        }
      });
    },

    // 保存当前更改
    saveCurrentChanges() {
      // 保存当前围栏未保存更改（通过父组件回调），并在失败时回滚
      if (!this.pendingChanges || this.savingChanges) return;

      this.savingChanges = true;

      // 使用父组件的 saveOne 方法保存更改
      this.$emit('saveOne', this.pendingChanges, false, (success) => {
        this.savingChanges = false;

        // 回调函数处理保存结果
        if (!success) {
          // 保存失败，恢复原始形状
          if (originalShape && originalShape.length > 0) {
            this.safelyRestoreShape(layer, originalShape);
            this.$Notice.error({
              title: langMap.tip[this.lang],
              desc: langMap.saveFailed[this.lang]
            });
          }
        }else{
          this.hasUnsavedChanges = false;
          this.originalLayerShapes = {};

          // 保存成功后重置拖拽模式状态
          this.isDragModeActive = false;
        }
      });
    },

    // 取消当前更改
    cancelCurrentChanges(pointNoHas) {
      // 取消当前未保存更改，回滚围栏形状并恢复交互/选中状态
      this.$emit('areasRenderStart');
      const targetLayer = this.findLayerById(this.currentEditingFenceId);

      // 检查是否是第一次绘制的围栏（没有原始数据）
      const isNewlyDrawnFence = targetLayer && !this.originalLayerShapes[this.currentEditingFenceId];
      const shouldRecalcAfterCancel = !isNewlyDrawnFence;

      if (isNewlyDrawnFence) {
        // 对于第一次绘制的围栏，直接删除图层和数据
        this.removeNewlyDrawnFence(targetLayer);
      } else if (targetLayer && this.originalLayerShapes[this.currentEditingFenceId]) {
        // 对于已有数据的围栏，恢复到最初的原始形状
        this.safelyRestoreShape(targetLayer, this.originalLayerShapes[this.currentEditingFenceId], pointNoHas);

        // 恢复本地数据到原始状态
        if (this.pendingChanges) {
          // 找到对应的原始数据项并恢复
          const originalItem = this.areaList.find(item => item.tempId === this.currentEditingFenceId);
          if (originalItem && this.originalLayerShapes[this.currentEditingFenceId]) {
            // 恢复原始的area数据
            const originalShape = this.originalLayerShapes[this.currentEditingFenceId];
            let areas = [];
            originalShape.forEach((point, index) => {
              let type;
              if (this.saveForm.coordinateType === "WGS") {
                type = 0;
              } else if (
                this.saveForm.coordinateType === "PLI" ||
                this.saveForm.coordinateType === "PLO"
              ) {
                type = 1;
              }
              areas[index] = {
                lon: point.lng,
                lat: point.lat,
                type: type
              };
            });
            originalItem.area = JSON.stringify(areas);

            // 更新父组件的数据
            this.$emit('updateAreaList', this.areaList);
          }
        }
      }

      // 保存当前编辑的围栏ID，用于保持选中状态
      const currentFenceId = this.currentEditingFenceId;

      // 重置编辑状态
      this.hasUnsavedChanges = false;
      this.pendingChanges = null;
      this.currentEditingFenceId = null;

      // 取消更改时重置拖拽模式状态
      this.isDragModeActive = false;

      // 重新启用其他围栏的交互
      this.enableOtherFencesInteraction();

      // 保持当前围栏的选中状态（焦点状态），并重新启用编辑模式
      if (currentFenceId && targetLayer) {
        // 重新启用编辑模式和拖拽功能
        if (targetLayer.pm) {
          targetLayer.pm.enable({
            allowSelfIntersection: false,
          });

          // 重新绑定拖拽事件
          targetLayer.off("pm:dragstart pm:drag pm:dragend");
          targetLayer.on("pm:dragstart", e => this.handleDragStart(e));
          targetLayer.on("pm:drag", e => this.handleDrag(e));
          targetLayer.on("pm:dragend", e => this.handleDragEndFence(e));
        }

        // 保持高亮显示（完全不透明）
        if (targetLayer._path) {
          targetLayer._path.style.opacity = '1';
        }
        // 保持当前围栏ID，用于焦点状态
        this.currentFenceId = currentFenceId;
      }
      if (shouldRecalcAfterCancel) {
        this.scheduleIntersectionsRecalc();
      }
    },

    // 删除新绘制的围栏（第一次绘制时取消）
    removeNewlyDrawnFence(targetLayer) {
      // 删除新绘制但未保存的围栏图层与本地数据
      if (!targetLayer) return;
      this.$emit('areasRenderStart');

      const fenceId = targetLayer.options.id;

      // 从地图中移除图层
      if (this.map.hasLayer(targetLayer)) {
        this.map.removeLayer(targetLayer);
      }

      // 从 areaLayers 数组中移除
      const layerIndex = this.areaLayers.findIndex(layer =>
        layer && layer.options && layer.options.id === fenceId
      );
      if (layerIndex !== -1) {
        this.areaLayers.splice(layerIndex, 1);
      }

      // 清空对应数据项的 area 字段，但保留数据记录
      const dataIndex = this.areaList.findIndex(item => item.tempId === fenceId);
      if (dataIndex !== -1) {
        // 清空 area 字段，这样绘制按钮会重新出现
        this.$set(this.areaList[dataIndex], 'area', null);
        // 更新父组件的数据
        this.$emit('updateAreaList', this.areaList);
      }

      // 清理相关状态
      if (this.currentFenceId === fenceId) {
        this.currentFenceId = null;
      }
      if (this.currentEditingFenceId === fenceId) {
        this.currentEditingFenceId = null;
      }

      // 重新计算并显示交叉区域
      this.scheduleIntersectionsRecalc();

      this.$Message.info(langMap.cancelDrawSuccess[this.lang]);
    },

    cancelCollectPreviewEditsWithMask() {
      // 取消采集预览的未保存编辑（触发父组件遮罩与回滚）
      this.$emit('areasRenderStart');
      this.$emit('cancelCollectPreviewEdits');
    },

    /**
     * 取消采集预览围栏（带遮罩）：触发父组件显示渲染遮罩并执行预览清理。
     * @param {boolean} restoreMode - 是否恢复进入采集预览前的编辑模式
     */
    cancelCollectPreviewWithMask(restoreMode = true) {
      // 取消采集预览围栏（触发父组件清理预览，可选恢复模式）
      this.$emit('areasRenderStart');
      this.$emit('cancelCollectPreview', restoreMode);
    },

    // 禁用其他围栏的交互
    disableOtherFencesInteraction() {
      // 降低其它围栏透明度以提示不可交互（编辑未保存时使用）
      this.areaLayers.forEach(layer => {
        if (layer.options.id !== this.currentEditingFenceId) {
          // 设置半透明样式表示不可交互
          if (layer._path) {
            layer._path.style.opacity = '0.3';
            layer._path.style.pointerEvents = 'auto';
          }
        }
      });
    },

    // 重新启用其他围栏的交互
    enableOtherFencesInteraction() {
      // 恢复其它围栏的点击与显示样式（取消/保存后使用）
      let _this = this;
      this.areaLayers.forEach(layer => {
        // 重新添加点击事件（对所有围栏）
        const fenceData = this.findFenceById(layer.options.id);
        if (fenceData) {
          // 先移除旧的点击事件，避免重复绑定
          layer.off("click");
          layer.on("click", e => {
            const didHandle = _this.createPopup(fenceData.tempId, e.latlng);
            if (didHandle) {
              _this.currentFenceId = fenceData.tempId;
            }
          });
        }

        // 恢复样式，但要区分当前选中的围栏和其他围栏
        if (layer._path) {
          layer._path.style.pointerEvents = 'auto';

          // 如果是当前选中的围栏，保持高亮状态
          if (layer.options.id === this.currentFenceId) {
            layer._path.style.opacity = '1'; // 保持高亮
          } else {
            // 其他围栏根据编辑模式设置透明度
            const opacity = this.isEdit ? '0.5' : '1';
            layer._path.style.opacity = opacity;
          }
        }
      });
    },
    // 退出编辑模式的统一方法
    exitEditMode() {
      // 退出编辑模式并重置编辑/拖拽状态，恢复所有图层正常显示
      if (this.currentEditingFenceId) {
        this.cancelCurrentChanges(true);
      }
      // 重置所有编辑相关状态
      this.hasUnsavedChanges = false;
      this.pendingChanges = null;
      this.currentEditingFenceId = null;

      // 重置拖拽模式状态
      this.isDragModeActive = false;

      // 通知父组件没有选中的围栏
      this.$emit('activeFenceChanged', null);

      // 恢复所有图层的显示
      this.areaLayers.forEach(layer => {
        if (layer && layer.pm) {
          layer.pm.disable();
          // 禁用拖拽模式
          if (layer.pm.disableLayerDrag) {
            layer.pm.disableLayerDrag();
          }
        }
        if (layer._path) {
          // 恢复图层的透明度为完全不透明
          layer._path.style.opacity = '1';
          layer._path.style.display = '';
          layer._path.style.pointerEvents = 'auto';
        }
      });

      // 清除编辑提示信息
      this.removeEditTipLayer();

      // 关闭全局编辑模式
      this.map.pm.disableGlobalEditMode();

      // 重置当前选中的围栏ID
      this.currentFenceId = null;
    },
    // 处理围栏拖拽开始事件
    handleDragStart(e) {
      // 记录拖拽开始快照与状态，并禁用其它围栏交互
      // 记录拖拽开始时的围栏ID
      this.currentEditingFenceId = e.target.options.id;
      this.currentFenceId = e.target.options.id; // 立即设置当前围栏ID

      // 标记拖拽模式为激活状态
      this.isDragModeActive = true;

      // 保存当前拖拽开始时的形状（用于单次拖拽的回滚）
      this.currentDragStartShape = e.target.getLatLngs()[0].map(latlng => ({
        lat: latlng.lat,
        lng: latlng.lng
      }));

      // 立即禁用其他围栏交互，确保状态同步
      this.disableOtherFencesInteraction();

      // 显示拖拽提示
      this.$Message.info('开始拖拽围栏');
    },

    // 处理围栏拖拽过程事件
    handleDrag(e) {
      // 拖拽过程节流与渲染优化，提高拖拽流畅性
      // 使用节流来优化拖拽性能
      if (this.dragThrottleTimer) {
        clearTimeout(this.dragThrottleTimer);
      }

      this.dragThrottleTimer = setTimeout(() => {
        // 拖拽过程中的优化处理
        // 减少不必要的重绘来提高流畅性
        if (e.target && e.target._path) {
          // 临时禁用过渡动画，提高拖拽流畅性
          e.target._path.style.transition = 'none';
          // 优化渲染性能
          e.target._path.style.willChange = 'transform';
        }

        // 实时更新编辑点位置（如果存在）
        if (e.target.pm && e.target.pm._markers) {
          // PM插件会自动处理编辑点的位置更新
          // 这里可以添加额外的视觉反馈
        }
      }, 16); // 约60fps的更新频率
    },

    // 处理围栏拖拽结束事件
    handleDragEndFence(e) {
      // 拖拽结束后做冲突检测，并将坐标变更写入本地未保存更改
      this.$emit('areasRenderStart');

      // 清理拖拽性能优化设置
      if (this.dragThrottleTimer) {
        clearTimeout(this.dragThrottleTimer);
        this.dragThrottleTimer = null;
      }

      // 恢复过渡动画和清理性能优化
      if (e.target && e.target._path) {
        //e.target._path.style.transition = 'all 0.2s ease';
        e.target._path.style.willChange = 'auto'; // 清理willChange优化
      }

      // 记录当前编辑的电子围栏ID
      this.currentFenceId = e.target.options.id;
      this.currentEditingFenceId = e.target.options.id;

      // 检查是否与同优先级图层产生交叉
      const hasConflict = this.checkEditingConflict(e.target);
      if (hasConflict) {
        this.handleConflict(e.target, e.target.options.id);
        // 拖拽冲突时也要重置拖拽模式状态
        this.isDragModeActive = false;
        this.scheduleIntersectionsRecalc();
        return;
      }

      // 注意：这里不重置拖拽模式状态，保持拖拽状态直到用户保存或取消
      // this.isDragModeActive = false; // 移除这行，让拖拽状态持续

      // 没有冲突，更新围栏位置数据
      this.updateAreaAfterDrag(e.target);
    },
    // 拖拽后更新围栏数据
    updateAreaAfterDrag(layer) {
      // 将拖拽后的围栏坐标同步到 areaList，并标记未保存
      // 记录当前编辑的电子围栏ID
      this.currentFenceId = layer.options.id;
      this.currentEditingFenceId = layer.options.id;

      this.areaList.forEach((item, index, arr) => {
        if (
          layer &&
          layer.options &&
          layer.options.id &&
          layer.options.id === item.tempId
        ) {
          if (layer._latlngs[0]) {
            let areas = [];
            layer._latlngs[0].forEach((item1, index1, arr1) => {
              let type;
              if (this.saveForm.coordinateType === "WGS") {
                type = 0;
              } else if (
                this.saveForm.coordinateType === "PLI" ||
                this.saveForm.coordinateType === "PLO"
              ) {
                type = 1;
              }
              let point = {
                lon: item1.lng,
                lat: item1.lat,
                type: type
              };
              areas[index1] = point;
            });
            let area = JSON.stringify(areas);

            // 不再自动保存，而是保存待保存的更改
            const updatedItem = { ...item, area: area };
            this.pendingChanges = updatedItem;
            this.hasUnsavedChanges = true;

            // 强制刷新编辑点位位置
            if (layer.pm) {
              layer.pm.disable();
              setTimeout(() => {
                layer.pm.enable();
              }, 10);
            }

            // 禁用其他围栏的点击
            this.disableOtherFencesInteraction();

            // 更新本地数据用于显示
            this.$set(this.areaList, index, updatedItem);
            // 父组件更新列表数据
            this.$emit('updateAreaList', this.areaList);
            // 显示拖拽完成的提示
            this.$Message.success(`${langMap.fenceDragCompleted[this.lang]}: ${item.name}`);
          }
        }
      });
      this.scheduleIntersectionsRecalc();
    }
  }
};
</script>
<style scoped>
.leaflet-component-box {
  height: 100%;
  position: relative;
}

/* 图层排序面板样式 - 高级版 */
.layer-sort-panel {
  position: absolute;
  top: 15px;
  right: 15px;
  background: linear-gradient(145deg, #ffffff, #f8f9fc);
  border-radius: 12px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12);
  padding: 18px;
  z-index: 1000;
  width: 280px;
  max-height: 80vh;
  overflow-y: auto;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(240, 240, 240, 0.8);
  backdrop-filter: blur(10px);
  transform-origin: top right;
  animation: panelFadeIn 0.3s ease forwards;
}

@keyframes panelFadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.panel-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  text-align: center;
  color: #1890ff;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(24, 144, 255, 0.15);
  position: relative;
}

.panel-title:after {
  content: "";
  position: absolute;
  bottom: -1px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 3px;
  background: linear-gradient(90deg, #1890ff, #40a9ff);
  border-radius: 3px;
}

.panel-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.panel-buttons button {
  padding: 10px 14px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(145deg, #f8f9fc, #f0f2f5);
  cursor: pointer;
  color: #555;
  font-size: 14px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.03);
  position: relative;
  overflow: hidden;
}

.panel-buttons button:before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  transition: all 0.6s ease;
}

.panel-buttons button:hover {
  background: linear-gradient(145deg, #e6f7ff, #bae7ff);
  color: #1890ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(24, 144, 255, 0.15);
}

.panel-buttons button:hover:before {
  left: 100%;
}

.panel-buttons button:active {
  transform: translateY(0);
  box-shadow: 0 2px 3px rgba(24, 144, 255, 0.1);
}

/* 为每个按钮添加图标 */
.panel-buttons button:nth-child(1):before {
  content: "⬆️";
  position: absolute;
  left: 12px;
  font-size: 14px;
}

.panel-buttons button:nth-child(2):before {
  content: "↑";
  position: absolute;
  left: 12px;
  font-size: 14px;
}

.panel-buttons button:nth-child(3):before {
  content: "↓";
  position: absolute;
  left: 12px;
  font-size: 14px;
}

.panel-buttons button:nth-child(4):before {
  content: "⬇️";
  position: absolute;
  left: 12px;
  font-size: 14px;
}

.panel-buttons button {
  padding-left: 32px;
  text-align: left;
  justify-content: flex-start;
}

.panel-close {
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  font-size: 16px;
  width: 26px;
  height: 26px;
  text-align: center;
  line-height: 26px;
  border-radius: 50%;
  color: #1890ff;
  transition: all 0.3s ease;
  background: rgba(24, 144, 255, 0.1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.panel-close:hover {
  background: rgba(24, 144, 255, 0.2);
  transform: rotate(90deg);
  color: #096dd9;
}

/* 方法二：使用/deep/或::v-deep来处理全局样式 */
/deep/ .sort-layer-btn {
  background: linear-gradient(135deg, #1890ff, #096dd9);
  color: white !important;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  margin-top: 10px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(24, 144, 255, 0.3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  font-weight: 500;
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
}

/deep/ .sort-layer-btn:before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: all 0.6s ease;
}

/deep/ .sort-layer-btn:hover {
  background: linear-gradient(135deg, #40a9ff, #1890ff);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(24, 144, 255, 0.4);
}

/deep/ .sort-layer-btn:hover:before {
  left: 100%;
}

/deep/ .sort-layer-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(24, 144, 255, 0.3);
}

/* 修改弹出窗口内容样式 */
/deep/ .content-window-card {
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

/deep/ .content-window-card h4 {
  margin: 0 0 10px 0;
  font-size: 16px;
  color: #333;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 8px;
  white-space: pre-wrap;
  word-break: break-word;
}

/deep/ .input-item {
  margin: 5px 0;
  font-size: 13px;
  color: #666;
  line-height: 1.5;
}

/* 添加新的样式 */
.priority-selector {
  margin: 10px 0 15px;
  padding: 10px;
  background: rgba(24, 144, 255, 0.05);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
}

.priority-label {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #1890ff;
}

.priority-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.priority-input {
  flex: 1;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
  text-align: center;
  font-size: 16px;
}

.priority-apply-btn {
  padding: 8px 15px;
  background: linear-gradient(135deg, #1890ff, #096dd9);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
}

.priority-apply-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(24, 144, 255, 0.2);
}

/* 图层列表样式 */
.layer-list-container {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid rgba(24, 144, 255, 0.15);
}

.layer-list-title {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 10px;
  color: #1890ff;
}

.layer-list {
  max-height: 200px;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.layer-item {
  padding: 8px 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.2s ease;
}

.layer-item:last-child {
  border-bottom: none;
}

.layer-item:hover {
  background: rgba(24, 144, 255, 0.1);
}

.current-layer {
  background: rgba(24, 144, 255, 0.15);
  font-weight: 500;
}

.layer-priority {
  min-width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #1890ff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
}

.layer-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 围栏工具面板样式 */
.fence-tools-panel {
  position: absolute;
  /* 对齐到 Leaflet 缩放控件右侧（缩放控件默认 left: 10px; width≈32px） */
  top: 10px;
  left: 52px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: auto;
}

.fence-tools-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: auto;
  padding: 12px 14px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #1677ff, #0958d9);
  box-shadow: 0 4px 12px rgba(22, 119, 255, 0.35);
  transition: all 0.2s ease;
}

.fence-tools-btn:hover {
  background: linear-gradient(135deg, #4096ff, #1677ff);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(22, 119, 255, 0.45);
}

.fence-tools-btn.active {
  background: linear-gradient(135deg, #7c3aed, #5b21b6);
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.35);
}

.fence-tools-btn svg {
  width: 20px;
  height: 20px;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.2));
}

.fence-tools-menu {
  background: #fff;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  min-width: 160px;
}

.fence-tools-menu-item {
  width: 100%;
  border: none;
  background: transparent;
  padding: 10px 12px;
  text-align: left;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #1f1f1f;
  transition: background 0.15s ease;
}

.fence-tools-menu-item-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.fence-tools-menu-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  /* 下拉菜单图标稍大一点，提升辨识度 */
  width: 20px;
  height: 20px;
}

.fence-tools-menu-icon svg {
  width: 18px;
  height: 18px;
}

.fence-tools-menu-item.cut .fence-tools-menu-icon {
  color: #ff4500;
}

.fence-tools-menu-item.merge .fence-tools-menu-icon {
  color: #7c3aed;
}

.fence-tools-menu-item:hover {
  background: rgba(22, 119, 255, 0.08);
}

.fence-tools-menu-item.is-disabled {
  color: rgba(0, 0, 0, 0.28);
  cursor: not-allowed;
  background: transparent;
}

.fence-tools-menu-item.is-disabled .fence-tools-menu-icon {
  color: rgba(0, 0, 0, 0.28);
}

.fence-tools-menu-item.is-disabled:hover {
  background: transparent;
}

.fence-tools-menu-item.cut:hover {
  background: rgba(255, 107, 34, 0.10);
}

.fence-tools-menu-item.merge:hover {
  background: rgba(124, 58, 237, 0.10);
}

.merge-mode-tips {
  background: #fff;
  padding: 12px;
  border-radius: 10px;
  font-size: 13px;
  color: #0958d9;
  font-weight: 500;
  display: flex;
  flex-direction: column;
  gap: 10px;
  border: 1px dashed rgba(22, 119, 255, 0.35);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05);
}

.merge-mode-actions {
  display: flex;
  gap: 8px;
}

.merge-confirm-btn {
  flex: 1;
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  font-weight: 600;
  color: #fff;
  background: rgba(82, 196, 26, 0.95);
}

.merge-cancel-btn {
  flex: 1;
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  font-weight: 600;
  color: #ff4d4f;
  background: rgba(255, 77, 79, 0.15);
}

.merge-preview-tips {
  background: #fff;
  padding: 10px 12px;
  border-radius: 10px;
  /* 合并预览提示字号略微降低，避免一行放不下 */
  font-size: 12px;
  color: #389e0d;
  font-weight: 600;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  border: 1px solid rgba(82, 196, 26, 0.25);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05);
}

.merge-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.merge-preview-message {
  white-space: nowrap;
  font-size: 12px;
}

.merge-preview-form {
  display: flex;
  align-items: center;
  gap: 6px;
}

.merge-preview-actions {
  display: flex;
  justify-content: flex-end;
}

.merge-preview-name-label {
  white-space: nowrap;
  font-size: 12px;
}

.merge-preview-name-input {
  flex: 1;
  min-width: 0;
  height: 26px;
  padding: 0 8px;
  font-size: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  outline: none;
}

.merge-preview-name-input:focus {
  border-color: rgba(22, 119, 255, 0.6);
}

.merge-save-btn {
  border: none;
  border-radius: 6px;
  width: 100%;
  padding: 6px 10px;
  cursor: pointer;
  font-weight: 600;
  font-size: 12px;
  color: #fff;
  background: rgba(22, 119, 255, 0.95);
}

.merge-save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.merge-cancel-preview-btn {
  border: none;
  border-radius: 6px;
  padding: 4px 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 12px;
  color: #ff4d4f;
  background: rgba(255, 77, 79, 0.15);
}

/* 保存面板样式 */
.save-panel {
  position: absolute;
  top: 15px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1001;
  background: linear-gradient(145deg, #ffffff, #f8f9fc);
  border-radius: 12px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12);
  padding: 16px 20px;
  border: 1px solid rgba(240, 240, 240, 0.8);
  backdrop-filter: blur(10px);
  animation: savePanel-fadeIn 0.3s ease forwards;
}

@keyframes savePanel-fadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0) scale(1);
  }
}

.save-panel-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.save-panel-message {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fa8c16;
  font-size: 14px;
  font-weight: 500;
}

.save-panel-message svg {
  color: #fa8c16;
}

.save-panel-buttons {
  display: flex;
  gap: 8px;
}

.save-btn, .cancel-btn {
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
}

.save-btn {
  background: linear-gradient(135deg, #52c41a, #389e0d);
  color: white;
}

.save-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #73d13d, #52c41a);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(82, 196, 26, 0.3);
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cancel-btn {
  background: linear-gradient(135deg, #ff7875, #ff4d4f);
  color: white;
}

.cancel-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #ff9c99, #ff7875);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(255, 120, 117, 0.3);
}

.cancel-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}



.cut-fence-btn {
  padding: 12px 16px;
  background: linear-gradient(145deg, #ff6b22, #ff4500);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  color: white;
  font-weight: 600;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 4px 12px rgba(255, 107, 34, 0.4);
  transition: all 0.3s ease;
  letter-spacing: 1px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.cut-fence-btn:hover {
  background: linear-gradient(145deg, #ff7b32, #ff5500);
  transform: translateY(-3px);
  box-shadow: 0 6px 15px rgba(255, 107, 34, 0.5);
}

.cut-fence-btn.active {
  background: linear-gradient(135deg, #ff4500, #cc3700);
  color: white;
  box-shadow: 0 4px 12px rgba(204, 55, 0, 0.5);
  transform: translateY(0);
}

.cut-fence-btn svg {
  width: 20px;
  height: 20px;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.2));
}

.cut-mode-tips {
  background: #fff;
  padding: 12px;
  border-radius: 10px;
  font-size: 14px;
  color: #ff4500;
  font-weight: 500;
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: center;
  border: 1px dashed rgba(255, 69, 0, 0.3);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05);
}

.cancel-cut-btn {
  background: rgba(255, 77, 79, 0.2);
  color: #ff4d4f;
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(255, 77, 79, 0.2);
}

.cancel-cut-btn:hover {
  background: rgba(255, 77, 79, 0.3);
  transform: translateY(-2px);
  box-shadow: 0 3px 6px rgba(255, 77, 79, 0.3);
}

/* 切割点样式优化 */
/deep/ .leaflet-interactive {
  transition: all 0.2s ease;
}

/* 切割执行按钮样式 */
/deep/ .cut-popup .leaflet-popup-content-wrapper {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  border: 2px solid rgba(82, 196, 26, 0.3);
}

/deep/ .cut-popup .leaflet-popup-content {
  margin: 10px;
  text-align: center;
}

/deep/ .cut-execute-btn {
  background: linear-gradient(135deg, #ff6b22, #ff4500);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 3px 8px rgba(255, 107, 34, 0.4);
  letter-spacing: 1px;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
}

/deep/ .cut-execute-btn:hover {
  background: linear-gradient(135deg, #ff7b32, #ff5500);
  transform: translateY(-2px);
  box-shadow: 0 5px 12px rgba(255, 107, 34, 0.5);
}

/deep/ .cut-execute-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 5px rgba(255, 107, 34, 0.4);
}

/* 切割线样式 */
/deep/ .leaflet-interactive.leaflet-path-draggable {
  animation: pulseLine 2s infinite;
}

@keyframes pulseLine {
  0% {
    opacity: 0.7;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.7;
  }
}

/* 编辑模式提示样式 */
.edit-mode-tip {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  padding: 5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.edit-mode-tip-content {
  display: flex;
  align-items: center;
  gap: 5px;
}

.edit-mode-icon {
  width: 24px;
  height: 24px;
}

.edit-mode-message {
  font-size: 14px;
  color: #333;
}

/* 添加到现有样式中 */

/* 编辑模式提示样式 */
/deep/ .edit-mode-tip {
  background: white;
  padding: 8px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin: 10px;
  max-width: 250px;
  z-index: 1000;
  pointer-events: auto;
}

/deep/ .edit-mode-tip-content {
  display: flex;
  align-items: center;
  padding: 8px;
}

/deep/ .edit-mode-icon {
  margin-right: 10px;
  color: #ff6b22;
  display: flex;
  align-items: center;
  justify-content: center;
}

/deep/ .edit-mode-message {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

/* 当鼠标悬停在围栏上时的样式 */
/deep/ .leaflet-interactive:hover {
  cursor: pointer;
}
</style>
